<div class="min-h-screen pb-12 bg-gray-50 text-slate-800">
  <main class="w-full px-4 sm:px-6 lg:px-8 py-8">

    <!-- ============================================================ -->
    <!-- Header consolidado: breadcrumb + título + metadata           -->
    <!-- ============================================================ -->
    <div class="mb-6" *ngIf="enrollment">
      <!-- Breadcrumb compacto -->
      <nav class="flex items-center gap-2 text-xs font-medium text-slate-500 uppercase tracking-wider mb-2"
        aria-label="Breadcrumb">
        <a [routerLink]="['/dashboard']" class="hover:text-slate-700 transition-colors">Dashboard</a>
        <span class="text-slate-300">/</span>
        <a [routerLink]="['/dashboard/enrollments']" class="hover:text-slate-700 transition-colors">Inscripciones</a>
        <span class="text-slate-300">/</span>
        <span class="text-slate-700 tabular-nums">#{{ enrollment.id }}</span>
      </nav>

      <!-- Título + estado -->
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="min-w-0">
          <div class="flex items-baseline gap-2 flex-wrap">
            <h1 class="text-2xl font-semibold text-slate-900 tracking-tight">
              Inscripción #<span class="tabular-nums">{{ enrollment.id }}</span>
            </h1>
            <span class="pill"
              [ngClass]="(enrollment.status === 1 || enrollment.status === 'Active') ? 'pill-emerald'
                : (enrollment.status === 6 || enrollment.status === 'Reversed') ? 'pill-rose'
                : 'pill-amber'">
              <span *ngIf="enrollment.status === 1 || enrollment.status === 'Active'"
                class="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
              {{ getEnrollmentStatusLabel(enrollment.status) }}
            </span>
          </div>
          <p class="text-sm text-slate-500 mt-1">Inscripción Individual · Gestión financiera y seguimiento de pagos.</p>

          <!-- Meta-strip: auditoría inline (sin caja redundante) -->
          <div class="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3">
            <span class="meta-pill">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Creada <strong>{{ enrollment.createdAt | date:'dd/MM/yy HH:mm' }}</strong>
            </span>
            <span class="meta-pill" *ngIf="enrollment.createdBy">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              por <strong>{{ enrollment.createdBy }}</strong>
            </span>
            <span class="meta-pill" *ngIf="enrollment.updatedAt">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Modificada <strong>{{ enrollment.updatedAt | date:'dd/MM/yy HH:mm' }}</strong>
              <span *ngIf="enrollment.updatedBy">· {{ enrollment.updatedBy }}</span>
            </span>
          </div>
        </div>

        <!-- Selector de estado (solo cuando hay permiso). Reemplaza la duplicación píldora+dropdown -->
        <div *ngIf="canUpdateEnrollmentStatus" class="flex items-center gap-2 shrink-0">
          <label class="text-xs font-medium text-slate-500 uppercase tracking-wider">Cambiar estado</label>
          <p-dropdown [options]="enrollmentStatusOptions" [(ngModel)]="selectedEnrollmentStatus" optionLabel="label"
            optionValue="value" (onChange)="onStatusChange($event.value)" placeholder="Estado"
            styleClass="filter-dropdown" appendTo="body" panelStyleClass="filter-dropdown-panel"
            [style]="{'min-width':'160px'}">
          </p-dropdown>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- Banners de alerta (paleta unificada)                         -->
    <!-- ============================================================ -->
    <div class="space-y-3 mb-6">
      <!-- ⚠️ Cobro automático DESACTIVADO -->
      <div *ngIf="enrollment && enrollment.autoGeneratePaymentLinks === false && enrollment.status !== 6 && enrollment.status !== 'Reversed'"
           class="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 flex items-start gap-3">
        <div class="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center shrink-0">
          <svg class="w-5 h-5 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-bold text-yellow-900">Recaudo automático desactivado</p>
          <p class="text-xs text-yellow-800 mt-0.5">
            Esta inscripción tiene desactivado el recaudo automático vía Wompi.
            No se generarán links de pago automáticos para las cuotas pendientes.
            Si necesitás reactivarlo, usá el toggle <strong>Cobro automático</strong> en la sección del plan de pagos.
          </p>
        </div>
      </div>

      <!-- Cartera castigada global -->
      <div *ngIf="isEnrollmentWithdrawnOrDeferred() && !hasMovementHistory()" class="alert-banner alert-rose">
        <svg class="alert-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div class="alert-body">
          <p class="alert-title">Inscripción con cartera castigada</p>
          <p class="alert-text">Esta inscripción tiene movimientos de cartera castigada y no genera cobros activos automáticos.</p>
          <p class="alert-text mt-2" *ngIf="enrollment.withdrawalReason">
            <strong>Razón:</strong> {{ enrollment.withdrawalReason }}
          </p>
        </div>
      </div>

      <!-- Necesita atención: total inscripción ≠ suma de cuotas activas -->
      <div *ngIf="enrollment && needsAttentionInfo.needs" class="alert-banner alert-amber">
        <svg class="alert-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div class="alert-body">
          <p class="alert-title">Esta inscripción necesita atención</p>
          <p class="alert-text">El total asignado en cuotas no coincide con el total de la inscripción.</p>
          <p class="alert-text mt-1 tabular-nums">
            <strong>Total:</strong> {{ formatCurrencyValue(needsAttentionInfo.total, enrollment?.currencySymbol, enrollment?.currencyCode) }}
            · <strong>Cuotas activas:</strong> {{ formatCurrencyValue(needsAttentionInfo.sumActive, enrollment?.currencySymbol, enrollment?.currencyCode) }}
            · <strong>Diferencia:</strong> {{ formatCurrencyValue(needsAttentionInfo.delta, enrollment?.currencySymbol, enrollment?.currencyCode) }}
          </p>
          <!-- 🆕 CTA: redistribuir saldos editando el plan de pagos -->
          <button type="button" (click)="onEditPlanClick()"
                  [disabled]="isEditPaymentPlanDisabled() || isRecalculatingAll"
                  class="mt-2 inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-40">
            {{ isRecalculatingAll ? 'Recalculando…' : 'Editar plan de pagos' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- KPIs unificados (mismo patrón que comprobantes)              -->
    <!-- ============================================================ -->
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" *ngIf="enrollment">

      <!-- Valor Matrícula -->
      <div class="kpi-card kpi-sky">
        <div class="kpi-label">
          <span class="kpi-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          Valor Matrícula
          <!-- 🆕 Editar valor de matrícula (permiso ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS) -->
          <button *ngIf="isSuperAdmin" type="button" (click)="openEditAmountModal()"
                  title="Editar valor de matrícula"
                  class="ml-auto rounded p-0.5 text-sky-600 hover:bg-sky-100">
            <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
            </svg>
          </button>
        </div>
        <div class="kpi-value">{{ formatCurrencyValue(enrollment.totalAmount, enrollment?.currencySymbol, enrollment?.currencyCode) }}</div>
        <div class="kpi-hint" *ngIf="enrollment.discount > 0">
          <span class="text-emerald-700 font-semibold">{{ enrollment.discount | number:'1.0-1' }}% OFF</span>
          · −{{ formatCurrencyValue(enrollment.discountAmount, enrollment?.currencySymbol, enrollment?.currencyCode) }}
        </div>
        <div class="kpi-hint" *ngIf="!(enrollment.discount > 0)">Sin descuentos aplicados</div>
      </div>

      <!-- Total Pagado -->
      <div class="kpi-card kpi-emerald">
        <div class="kpi-label">
          <span class="kpi-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          Total Pagado
        </div>
        <div class="kpi-value">{{ formatCurrencyValue(enrollment.amountPaid, enrollment?.currencySymbol, enrollment?.currencyCode) }}</div>
        <div class="w-full bg-slate-100 rounded-full h-1 mt-1">
          <div class="bg-emerald-500 h-1 rounded-full transition-all"
            [style.width.%]="getTotalAmount() > 0 ? (((enrollment.amountPaid / getTotalAmount()) * 100) > 100 ? 100 : ((enrollment.amountPaid / getTotalAmount()) * 100)) : 0"></div>
        </div>
        <div class="kpi-hint tabular-nums">
          {{ getTotalAmount() > 0 ? ((enrollment.amountPaid / getTotalAmount()) * 100 | number:'1.0-1') : 0 }}% completado
        </div>
      </div>

      <!-- Saldo Pendiente -->
      <div class="kpi-card kpi-amber">
        <div class="kpi-label">
          <span class="kpi-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          Saldo Pendiente
        </div>
        <div class="kpi-value">{{ formatCurrencyValue(getSaldoPendienteSinCastigo(), enrollment?.currencySymbol, enrollment?.currencyCode) }}</div>
        <div class="kpi-hint truncate" *ngIf="getNextDueDate()">Próximo: {{ getNextDueDate() }}</div>
        <div class="kpi-hint" *ngIf="!getNextDueDate()">Sin cuotas próximas</div>
      </div>

      <!-- Vencido / Mora -->
      <div class="kpi-card kpi-rose">
        <div class="kpi-label">
          <span class="kpi-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </span>
          Vencido / Mora
        </div>
        <div class="kpi-value" [class.text-rose-700]="getOverdueAmount() > 0">
          {{ formatCurrencyValue(getOverdueAmount(), enrollment?.currencySymbol, enrollment?.currencyCode) }}
        </div>
        <div class="kpi-hint tabular-nums" *ngIf="getOverdueCount() > 0">
          {{ getOverdueCount() }} cuota{{ getOverdueCount() !== 1 ? 's' : '' }}<span *ngIf="getOverdueDays() > 0"> · {{ getOverdueDays() }}d</span>
        </div>
        <div class="kpi-hint" *ngIf="getOverdueCount() === 0">Sin vencimientos</div>
      </div>

      <!-- Cartera Castigada -->
      <div class="kpi-card kpi-violet">
        <div class="kpi-label">
          <span class="kpi-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          </span>
          Cartera Castigada
        </div>
        <div class="kpi-value" [class.text-rose-700]="getCastigadoAmount() > 0">
          {{ formatCurrencyValue(getCastigadoAmount(), enrollment?.currencySymbol, enrollment?.currencyCode) }}
        </div>
        <div class="kpi-hint tabular-nums" *ngIf="getCastigadoCount() > 0">
          {{ getCastigadoCount() }} cuota{{ getCastigadoCount() !== 1 ? 's' : '' }} no recuperable{{ getCastigadoCount() !== 1 ? 's' : '' }}
        </div>
        <div class="kpi-hint" *ngIf="getCastigadoCount() === 0">Sin castigos</div>
      </div>
    </div>

    <!-- Último pago recibido (prominente) -->
    <div *ngIf="enrollment && getLastPaymentDate()"
         class="mb-4 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
      <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span class="text-xs text-emerald-800">
        Último pago recibido:
        <strong>{{ getLastPaymentDate() | date:'mediumDate' }}</strong>
      </span>
    </div>

    <!-- Content Grid: Left (Info) & Right (Tables) -->
    <div class="grid grid-cols-1 lg:grid-cols-5 gap-6" *ngIf="enrollment">

      <!-- Left Column: Context Info -->
      <div class="lg:col-span-1 space-y-4">

        <!-- Student Card -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div class="px-3 py-2.5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <h3 class="text-sm font-semibold text-gray-900">Estudiante</h3>
            <a [routerLink]="['/dashboard/participants/profile-participant', enrollment.participant.id]" target="_blank"
              class="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Ver &rarr;</a>
          </div>
          <div class="p-3">
            <div class="flex items-center gap-2 mb-3">
              <div
                class="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                {{ enrollment.participant.firstName?.charAt(0) }}{{ enrollment.participant.lastName?.charAt(0) }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-bold text-sm text-gray-900 truncate">{{ enrollment.participant.firstName }} {{
                  enrollment.participant.lastName }}</p>
                <p class="text-xs text-gray-500 truncate">{{ enrollment.participant.typeDocument }} {{
                  enrollment.participant.documentId }}</p>
              </div>
            </div>
            <div class="space-y-2 text-xs">
              <div class="flex items-start gap-2">
                <svg class="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span class="text-gray-600 truncate">{{ enrollment.participant.email }}</span>
              </div>
              <div class="flex items-start gap-2">
                <svg class="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span class="text-gray-600">{{ enrollment.participant.cellPhone }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Project Card -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div class="px-3 py-2.5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <h3 class="text-sm font-semibold text-gray-900">Proyecto</h3>
            <a [routerLink]="['/dashboard/projects/view-project', enrollment.project.id]" target="_blank"
              class="text-xs text-indigo-600 hover:text-indigo-800 font-medium">Ver &rarr;</a>
          </div>
          <div class="p-3">
            <h4 class="font-bold text-sm text-gray-900 mb-0.5 truncate">{{ enrollment.project.name }}</h4>
            <p class="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-3">{{ enrollment.project.projectId
              }}</p>

            <div class="space-y-1.5 text-xs">
              <div class="flex justify-between py-1 border-b border-gray-100">
                <span class="text-gray-500">Duración</span>
                <span class="font-medium text-gray-800">{{ enrollment.project.duration }} Sem</span>
              </div>
              <div class="flex justify-between py-1 border-b border-gray-100">
                <span class="text-gray-500">Período</span>
                <span class="font-medium text-gray-800 truncate ml-2">{{ enrollment.project.period }}</span>
              </div>
              <div class="flex justify-between py-1">
                <span class="text-gray-500">Estado</span>
                <span class="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{{
                  enrollment.project.status }}</span>
              </div>
            </div>
          </div>
        </div>


        <!-- Observations Card (append-only) -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div class="px-3 py-2.5 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <h3 class="text-sm font-semibold text-gray-900">Observaciones</h3>
            <button *ngIf="!isAddingObservation"
                    (click)="startAddingObservation()"
                    class="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Agregar
            </button>
          </div>
          <div class="p-3">
            <div *ngIf="!isAddingObservation">
              <div *ngIf="enrollment.observations"
                   class="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-2 max-h-48 overflow-y-auto">{{ enrollment.observations }}</div>
              <p *ngIf="!enrollment.observations" class="text-sm text-gray-400 italic">Sin observaciones</p>
            </div>
            <ng-container *ngIf="isAddingObservation">
              <p class="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Nueva observación</p>
              <textarea [(ngModel)]="newObservationDraft" rows="3"
                        maxlength="2000"
                        placeholder="Escribí una nueva observación. Se agregará al historial con fecha + usuario."
                        class="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"></textarea>
              <div class="flex justify-between items-center mt-2">
                <span class="text-[10px] text-gray-500 tabular-nums">{{ newObservationDraft.length }}/2000</span>
                <div class="flex gap-2">
                  <button (click)="cancelAddingObservation()"
                          [disabled]="isSavingObservation"
                          class="text-xs text-gray-600 hover:text-gray-800 px-2 py-1">Cancelar</button>
                  <button (click)="saveNewObservation()"
                          [disabled]="isSavingObservation || newObservationDraft.trim().length < 3"
                          class="text-xs px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                    {{ isSavingObservation ? 'Guardando...' : 'Agregar al historial' }}
                  </button>
                </div>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Timeline Mini Widget -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-3" *ngIf="hasMovementHistory()">
          <h3 class="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg> Movimientos
          </h3>
          <div class="relative pl-3 border-l border-gray-200 space-y-4">
            <div *ngFor="let movement of movementHistory; let i = index" class="relative">
              <div class="absolute -left-[17px] h-2 w-2 rounded-full border border-white"
                [ngClass]="getTimelineDotClass(movement.movementType)"></div>
              <div class="flex items-start justify-between gap-2">
                <div class="flex-1">
                  <p class="text-xs text-gray-400 mb-0.5">{{ movement.movementDate | date:'shortDate' }}</p>
                  <p class="text-xs font-medium text-gray-800">{{ movement.movementTypeDescription }}</p>
                  <p class="text-xs text-gray-500">{{ movement.totalAffectedAmount | currency }} • {{
                    movement.affectedInstallmentsCount }} cuota(s)</p>
                </div>
                <button (click)="openMovementDetail(movement)"
                  class="text-gray-400 hover:text-indigo-600 transition-colors p-1 rounded hover:bg-gray-100"
                  title="Ver detalles">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Main Tables -->
      <div class="lg:col-span-4 space-y-6">

        <!-- Payment Plan Section -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          [class.border-red-200]="enrollment.status === 6 || enrollment.status === 'Reversed'">

          <!-- Banner de Anulación (no obstructivo, permite ver el plan en modo lectura) -->
          <div *ngIf="enrollment.status === 6 || enrollment.status === 'Reversed'"
            class="bg-red-50 border-b border-red-200 px-4 py-3 flex items-center gap-3">
            <div class="bg-red-100 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-red-800">Plan de Pagos Anulado</p>
              <p class="text-xs text-red-600">Esta inscripción ha sido reversada. El plan de pagos es de solo lectura.</p>
            </div>
            <div class="inline-flex items-center px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium flex-shrink-0">
              <span class="w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5"></span>
              Reversada
            </div>
          </div>

          <!-- ============================================================ -->
          <!-- Toolbar del Plan de Pagos: jerarquía clara                  -->
          <!-- ============================================================ -->
          <div class="px-6 py-4 border-b border-slate-100 bg-white space-y-4">

            <!-- Título + meta (estado de cuotas inline) -->
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <h2 class="text-lg font-semibold text-slate-900">Plan de Pagos</h2>
                <p class="text-xs text-slate-500 mt-0.5 tabular-nums">
                  {{ enrollment.paymentPlans.length }} cuotas programadas
                </p>
                <!-- Estado de cuotas como pills inline -->
                <div *ngIf="enrollment.paymentPlans?.length" class="mt-2.5">
                  <app-installment-stats [plans]="enrollment.paymentPlans" variant="inline"></app-installment-stats>
                </div>
              </div>

              <!-- Toggle "Cobro automático" inline (sin caja amarilla) -->
              <ng-container *ngIf="enrollment.status !== 6 && enrollment.status !== 'Reversed'">
                <div *appHasPermission="'ENROLLMENTS_UPDATE'" class="toggle-inline shrink-0"
                  [title]="enrollment.autoGeneratePaymentLinks ? 'El sistema enviará links de pago automáticamente por email' : 'Los pagos se gestionan manualmente'">
                  <svg class="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Cobro automático</span>
                  <label class="toggle-switch">
                    <input type="checkbox" [checked]="enrollment.autoGeneratePaymentLinks" (change)="toggleAutoPayment($event)">
                    <span class="slider"></span>
                  </label>
                </div>
              </ng-container>
            </div>

            <!-- Acciones agrupadas: positivas a la izquierda, destructivas a la derecha -->
            <ng-container *ngIf="enrollment.status !== 6 && enrollment.status !== 'Reversed'">
              <div class="flex flex-wrap items-center justify-between gap-2">

                <!-- Grupo positivas: edición + consulta -->
                <div class="flex flex-wrap items-center gap-2">
                  <!-- 🆕 Recalcula pagos de todas las cuotas antes de abrir el editor -->
                  <button *appHasPermission="'ENROLLMENTS_PAYMENT_PLANS'"
                    type="button"
                    (click)="onEditPlanClick()"
                    [disabled]="isEditPaymentPlanDisabled() || isRecalculatingAll"
                    [title]="isEditPaymentPlanDisabled() ? 'Todas las cuotas están pagadas y no hay saldo pendiente, no hay nada que editar' : 'Recalcula los pagos y abre el editor del plan'"
                    [class.opacity-40]="isEditPaymentPlanDisabled()"
                    class="btn btn-primary btn-sm">
                    <svg *ngIf="!isRecalculatingAll" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                    </svg>
                    <svg *ngIf="isRecalculatingAll" class="animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {{ isRecalculatingAll ? 'Recalculando…' : 'Editar plan' }}
                  </button>

                  <!-- 🆕 Papelera de cuotas eliminadas (permiso ENROLLMENTS_PAYMENT_PLAN_TRASH) -->
                  <button *ngIf="canSeeTrash" type="button" (click)="openTrashModal()" class="btn btn-ghost btn-sm"
                    title="Ver cuotas eliminadas y restaurarlas">
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                    Papelera ({{ trashModal.count }})
                  </button>

                  <button type="button" (click)="goToReceipts()" class="btn btn-secondary btn-sm"
                    title="Ver los comprobantes de ingreso de este participante en este proyecto, en una nueva pestaña">
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Comprobantes
                    <svg class="w-3 h-3 -ml-1 opacity-60" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>

                  <a [routerLink]="['/dashboard/enrollments/payment-plans/history', enrollment.id]"
                    [queryParams]="{ type: 'individual' }" target="_blank" class="btn btn-ghost btn-sm" title="Historial del plan de pagos">
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Historial
                  </a>

                  <button *appHasPermission="'ENROLLMENTS_READ'" type="button" (click)="exportExcel()"
                    [disabled]="isExportingExcel" class="btn btn-ghost btn-sm"
                    title="Descargar Excel con el detalle de la inscripción, KPIs, plan de pagos y comprobantes">
                    <svg *ngIf="!isExportingExcel" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M4 6h16M4 6v12a2 2 0 002 2h12a2 2 0 002-2V6M4 6l2-3h12l2 3" />
                    </svg>
                    <svg *ngIf="isExportingExcel" class="animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    {{ isExportingExcel ? 'Generando…' : 'Excel' }}
                  </button>
                </div>

                <!-- Grupo destructivas: separado a la derecha -->
                <div class="flex flex-wrap items-center gap-2">
                  <button *appHasPermission="'ENROLLMENTS_WITHDRAW'" (click)="togglePunishMode()"
                    [disabled]="!isPunishMode && areAllPaymentPlansPaid()"
                    [title]="(!isPunishMode && areAllPaymentPlansPaid()) ? 'Todas las cuotas están pagadas, no hay cartera para castigar' : (isPunishMode ? 'Cancelar gestión de cartera' : 'Gestionar cartera castigada')"
                    class="btn btn-warning btn-sm">
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    {{ isPunishMode ? 'Cancelar gestión' : 'Gestionar cartera' }}
                  </button>

                  <button *appHasPermission="'ENROLLMENTS_DELETE'" (click)="openCancellationModal()"
                    class="btn btn-danger btn-sm" title="Anular inscripción">
                    <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    Anular
                  </button>
                </div>
              </div>
            </ng-container>
          </div>

          <!-- PUNISH MODE TOOLBAR (Hidden by default) -->
          <div *ngIf="isPunishMode"
            class="bg-red-50 border-b border-red-100 px-6 py-3 flex items-center justify-between animate-fade-in">
            <div class="flex items-center gap-3">
              <div class="bg-red-100 text-red-600 p-1.5 rounded">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <p class="text-sm font-bold text-red-900">Modo Gestión de Castigo</p>
                <p class="text-xs text-red-700">Selecciona cuotas para castigar o revertir.</p>
              </div>
            </div>
            <div class="flex gap-2">
              <input [(ngModel)]="punishReason" type="text" placeholder="Razón del cambio *"
                class="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500">
              <button (click)="togglePunishMode()"
                class="text-gray-600 hover:text-gray-800 text-sm font-medium px-3 py-1.5">Cancelar</button>
              <button (click)="executePunishAction('punish')" [disabled]="selectedPlanIds.length === 0 || !punishReason"
                class="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-1.5 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                Castigar
              </button>
              <button (click)="executePunishAction('unpunish')"
                [disabled]="selectedPlanIds.length === 0 || !punishReason"
                class="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-1.5 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                Descastigar
              </button>
            </div>
          </div>

          <!-- Table Container -->
          <div class="overflow-x-auto no-scrollbar">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th scope="col" *ngIf="isPunishMode" class="w-12 px-6 py-3 text-left">
                    <input type="checkbox" (change)="selectAllPlans()" [checked]="areAllPlansSelected()"
                      class="rounded border-gray-300 text-red-600 focus:ring-red-500 h-4 w-4">
                  </th>
                  <!-- 🆕 Columna handle de drag (solo super-admin) -->
                  <th scope="col" *ngIf="isSuperAdmin" class="w-8 px-2 py-3"
                      title="Arrastrá para reordenar las cuotas"></th>
                  <th scope="col"
                    class="w-12 px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"></th>
                  <th scope="col"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cuota</th>
                  <th scope="col"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    title="PaymentPlan.Id en DB">ID</th>
                  <th scope="col"
                    class="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vencimiento
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Monto</th>
                  <th scope="col"
                    class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Pagado
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    title="Monto pendiente de pago = Monto - Pagado">Saldo Restante
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado
                  </th>
                  <th scope="col"
                    class="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones
                  </th>
                </tr>
              </thead>
              <!-- 🆕 cdkDropList sobre el tbody — drag habilitado solo para super-admin -->
              <tbody class="bg-white divide-y divide-gray-200"
                     cdkDropList
                     [cdkDropListDisabled]="!isSuperAdmin"
                     (cdkDropListDropped)="onPlanDrop($event)">

                <ng-container *ngFor="let plan of enrollment.paymentPlans">
                  <!-- Row (draggable cuando isSuperAdmin) -->
                  <tr cdkDrag
                      [cdkDragData]="plan"
                      [cdkDragDisabled]="!isSuperAdmin"
                      cdkDragLockAxis="y"
                      [ngClass]="{
                    'bg-red-50/30 hover:bg-red-50/50 border-l-2 border-l-red-500': isPaymentOverdue(plan) && !plan.isPunished,
                    'bg-gray-50 text-gray-400 italic': plan.isPunished,
                    'hover:bg-gray-50': !plan.isPunished
                  }" class="group">
                    <td *ngIf="isPunishMode" class="px-6 py-4">
                      <input type="checkbox" [checked]="isPlanSelected(plan.id)" (change)="togglePlanSelection(plan.id)"
                        [disabled]="plan.paymentStatus === 'PAID'"
                        class="rounded border-gray-300 text-red-600 focus:ring-red-500 h-4 w-4 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    </td>
                    <!-- 🆕 Handle del drag (visible solo a super-admin) -->
                    <td *ngIf="isSuperAdmin" class="px-2 py-4 text-center text-gray-300 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                        cdkDragHandle
                        title="Arrastrar para reordenar">
                      <svg class="w-4 h-4 inline-block" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M7 4a1 1 0 100 2 1 1 0 000-2zM7 9a1 1 0 100 2 1 1 0 000-2zM7 14a1 1 0 100 2 1 1 0 000-2zM13 4a1 1 0 100 2 1 1 0 000-2zM13 9a1 1 0 100 2 1 1 0 000-2zM13 14a1 1 0 100 2 1 1 0 000-2z"/>
                      </svg>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-center">
                      <button (click)="toggleCollapse(plan)"
                        class="text-gray-400 hover:text-indigo-600 transition-colors">
                        <svg class="w-5 h-5 transition-transform" [ngClass]="{'rotate-180': !plan.isCollapsed}"
                          fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="flex items-center">
                        <span class="h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold mr-3"
                          [ngClass]="{
                            'bg-gray-100 text-gray-600': plan.paymentStatus === 'PAID',
                            'bg-white border border-red-200 text-red-600': isPaymentOverdue(plan) && !plan.isPunished,
                            'bg-gray-200 text-gray-500': plan.isPunished
                          }">
                          {{ plan.installmentNumber }}
                        </span>
                        <div>
                          <span *ngIf="plan.isCurrent && !plan.isPunished"
                            class="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700">ACTUAL</span>
                          <span *ngIf="plan.isPunished"
                            class="ml-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 not-italic">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg> Castigada
                          </span>
                          <!-- 🆕 Indicador de comprobantes anulados -->
                          <span *ngIf="hasCancelledReceipts(plan)"
                            class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200"
                            title="Esta cuota tiene comprobantes de pago anulados">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            {{ getCancelledReceipts(plan).length }} Anulado(s)
                          </span>
                          <!-- 🆕 Indicador de devoluciones -->
                          <span *ngIf="hasRefunds(plan)"
                            class="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200"
                            title="Esta cuota tiene devoluciones aplicadas">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                            {{ getRefundsForPlan(plan).length }} Devolución(es) - {{
                            formatCurrencyValue(getTotalRefundedForPlan(plan), enrollment?.currencySymbol,
                            enrollment?.currencyCode) }}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap font-mono text-[11px] text-gray-500"
                        title="PaymentPlan.Id">#{{ plan.id }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div *ngIf="!isPaymentOverdue(plan) || plan.isPunished" class="text-sm text-gray-500">
                        {{ plan.dueDate | date:'mediumDate' }}
                      </div>
                      <div *ngIf="isPaymentOverdue(plan) && !plan.isPunished">
                        <div class="text-sm text-red-600 font-medium">{{ plan.dueDate | date:'mediumDate' }}</div>
                        <div class="text-xs text-red-500">Vencida hace {{ getDaysOverdue(plan) }} días</div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right font-medium"
                      [ngClass]="{'text-gray-400': plan.isPunished, 'text-gray-900': !plan.isPunished}">
                      {{ formatCurrencyValue(plan.amountToPay, enrollment?.currencySymbol, enrollment?.currencyCode) }}
                      <span *ngIf="isInternationalEnrollment && !plan.isPunished"
                        class="block text-xs text-purple-600 mt-1">
                        ≈ {{ calculatePlanCOPEquivalent(plan, plan.amountToPay) }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right" [ngClass]="{
                      'text-emerald-600 font-medium': plan.paymentStatus === 'PAID',
                      'text-amber-600 font-medium': plan.paymentStatus === 'PARTIALLY_PAID' && plan.amountPaid > 0,
                      'text-gray-400': (plan.paymentStatus !== 'PAID' && !(plan.paymentStatus === 'PARTIALLY_PAID' && plan.amountPaid > 0)) || plan.isPunished
                    }">
                      <span *ngIf="plan.amountPaid > 0">{{ formatCurrencyValue(plan.amountPaid,
                        enrollment?.currencySymbol, enrollment?.currencyCode) }}</span>
                      <span *ngIf="!(plan.amountPaid > 0)">$0</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-right" [ngClass]="{
                      'text-gray-400 italic': plan.isPunished,
                      'text-emerald-600 font-medium': !plan.isPunished && (plan.amountToPay - plan.amountPaid) <= 0,
                      'text-rose-600 font-medium': !plan.isPunished && isPaymentOverdue(plan) && (plan.amountToPay - plan.amountPaid) > 0,
                      'text-gray-700': !plan.isPunished && !isPaymentOverdue(plan) && (plan.amountToPay - plan.amountPaid) > 0
                    }">
                      {{ formatCurrencyValue(plan.amountToPay - plan.amountPaid, enrollment?.currencySymbol, enrollment?.currencyCode) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-center">
                      <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                        'bg-emerald-100 text-emerald-800': plan.paymentStatus === 'PAID',
                        'bg-amber-100 text-amber-800': plan.paymentStatus === 'PENDING',
                        'bg-red-100 text-red-800': plan.paymentStatus === 'OVERDUE',
                        'bg-blue-100 text-blue-800': plan.paymentStatus === 'LINK_GENERATED'
                      }">
                        {{ getPaymentStatusLabel(plan.paymentStatus) }}
                      </span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div class="flex items-center justify-end gap-1">
                        <button *ngIf="plan.paymentStatus !== 'PAID' && !plan.isPunished && enrollment.status !== 6 && enrollment.status !== 'Reversed'"
                          [routerLink]="['/dashboard/income-receipts/new']"
                          [queryParams]="{participantId: enrollment.participant.id, projectId: enrollment.project.id, enrollmentId: enrollment.id, installmentNumber: plan.installmentNumber}"
                          class="text-indigo-600 hover:text-indigo-900 bg-indigo-50 px-3 py-1.5 rounded-md text-xs font-bold transition-colors">
                          Pagar
                        </button>

                        <!-- 🔐 Editar (solo super-admin): abre modal con todas las acciones -->
                        <button *ngIf="isSuperAdmin" (click)="openAdminMenu(plan)"
                          title="Acciones admin sobre esta cuota"
                          class="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors">
                          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>

                  <!-- Details Row (Collapsible) -->
                  <tr *ngIf="!plan.isCollapsed" class="bg-white border-t border-gray-100">
                    <td [attr.colspan]="(isPunishMode ? 10 : 9) + (isSuperAdmin ? 1 : 0)" class="px-6 py-4">
                      <div class="space-y-4">
                        <!-- Sistema de Tabs -->
                        <div class="border-b border-gray-200">
                          <nav class="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
                            <!-- Tab: Links de Pago -->
                            <button *ngIf="plan.paymentLinkUrl" (click)="setPlanTab(plan.id, 'payment-link')" [class]="isPlanTabActive(plan.id, 'payment-link') 
                                ? 'border-indigo-500 text-indigo-600' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              Link de Pago
                            </button>

                            <!-- Tab: Comprobantes de Ingreso -->
                            <button *ngIf="getActiveReceipts(plan).length > 0" (click)="setPlanTab(plan.id, 'receipts')"
                              [class]="isPlanTabActive(plan.id, 'receipts') 
                                ? 'border-emerald-500 text-emerald-600' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Comprobantes Activos
                              <span
                                class="ml-1 bg-emerald-100 text-emerald-600 py-0.5 px-2 rounded-full text-xs font-bold">
                                {{ getActiveReceipts(plan).length }}
                              </span>
                            </button>

                            <!-- Tab: Devoluciones -->
                            <button *ngIf="hasRefunds(plan)" (click)="setPlanTab(plan.id, 'refunds')" [class]="isPlanTabActive(plan.id, 'refunds') 
                                ? 'border-purple-500 text-purple-600' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              Devoluciones
                              <span
                                class="ml-1 bg-purple-100 text-purple-600 py-0.5 px-2 rounded-full text-xs font-bold">
                                {{ getRefundsForPlan(plan).length }}
                              </span>
                            </button>

                            <!-- Tab: Comprobantes Anulados -->
                            <button *ngIf="getCancelledReceipts(plan).length > 0"
                              (click)="setPlanTab(plan.id, 'cancelled')" [class]="isPlanTabActive(plan.id, 'cancelled') 
                                ? 'border-red-500 text-red-600' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                              Anulados
                              <span class="ml-1 bg-red-100 text-red-600 py-0.5 px-2 rounded-full text-xs font-bold">
                                {{ getCancelledReceipts(plan).length }}
                              </span>
                            </button>

                            <!-- Tab: Comprobantes Inactivos (versiones anteriores) -->
                            <button *ngIf="hasInactiveReceipts(plan)"
                              (click)="setPlanTab(plan.id, 'inactive')" [class]="isPlanTabActive(plan.id, 'inactive')
                                ? 'border-amber-500 text-amber-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                              Inactivos
                              <span class="ml-1 bg-amber-100 text-amber-600 py-0.5 px-2 rounded-full text-xs font-bold">
                                {{ getInactiveReceipts(plan).length }}
                              </span>
                            </button>

                            <!-- Tab: Historial de Anulaciones -->
                            <button *ngIf="plan.cancellationHistory && plan.cancellationHistory.length > 0"
                              (click)="setPlanTab(plan.id, 'history')" [class]="isPlanTabActive(plan.id, 'history')
                                ? 'border-orange-500 text-orange-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Historial
                            </button>

                            <!-- Tab: Emails Enviados -->
                            <button *ngIf="plan.paymentLinkId" (click)="setPlanTab(plan.id, 'emails'); loadEmailsForPlan(plan)" [class]="isPlanTabActive(plan.id, 'emails')
                                ? 'border-violet-500 text-violet-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Emails
                              <span *ngIf="hasEmails(plan)"
                                class="ml-1 bg-violet-100 text-violet-600 py-0.5 px-2 rounded-full text-xs font-bold">
                                {{ getEmailsForPlan(plan).length }}
                              </span>
                            </button>

                            <!-- Tab: Webhooks Wompi -->
                            <button *ngIf="plan.paymentLinkId" (click)="setPlanTab(plan.id, 'webhooks')" [class]="isPlanTabActive(plan.id, 'webhooks')
                                ? 'border-cyan-500 text-cyan-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                              class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors">
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.858 15.355-5.858 21.213 0" />
                              </svg>
                              Webhooks Wompi
                              <span *ngIf="hasWebhooks(plan)"
                                class="ml-1 bg-cyan-100 text-cyan-600 py-0.5 px-2 rounded-full text-xs font-bold">
                                {{ getWebhooksForPlan(plan).length }}
                              </span>
                            </button>
                          </nav>
                        </div>

                        <!-- Contenido de los Tabs -->
                        <div class="mt-4">
                          <!-- Tab Content: Link de Pago -->
                          <div *ngIf="isPlanTabActive(plan.id, 'payment-link') && plan.paymentLinkUrl"
                            class="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <h4 class="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                              <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              Link de Pago
                            </h4>
                            <app-payment-link-manager [paymentLinkUrl]="plan.paymentLinkUrl"
                              [paymentLinkId]="plan.paymentLinkId" [isActive]="plan.paymentLinkActive !== false"
                              [showDeactivateButton]="true" [size]="'sm'"
                              (paymentLinkDeactivated)="onPaymentLinkDeactivated()">
                            </app-payment-link-manager>
                          </div>

                          <!-- Tab Content: Emails Enviados -->
                          <div *ngIf="isPlanTabActive(plan.id, 'emails') && plan.paymentLinkId" class="space-y-3">
                            <div class="flex items-center justify-between mb-3">
                              <h4 class="text-sm font-bold text-gray-700 flex items-center gap-2">
                                <svg class="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Emails Enviados
                              </h4>
                            </div>

                            <!-- Loading -->
                            <div *ngIf="isEmailsLoading(plan)" class="flex items-center justify-center py-6">
                              <svg class="animate-spin h-5 w-5 text-violet-500 mr-2" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                              </svg>
                              <span class="text-sm text-gray-500">Cargando emails...</span>
                            </div>

                            <!-- Sin emails -->
                            <div *ngIf="!isEmailsLoading(plan) && !hasEmails(plan)"
                              class="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                              <svg class="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <p class="text-sm text-gray-500">No se han enviado emails para este link de pago.</p>
                            </div>

                            <!-- Lista de emails -->
                            <div *ngFor="let email of getEmailsForPlan(plan)"
                              class="bg-white border rounded-lg shadow-sm p-4">
                              <div class="flex items-center justify-between">
                                <div class="flex items-center gap-3 flex-1 min-w-0">
                                  <!-- Status Badge -->
                                  <span class="text-xs font-bold px-2 py-0.5 rounded flex-shrink-0"
                                    [ngClass]="emailLogService.getSuccessClass(email.isSuccess)">
                                    {{ email.isSuccess ? 'Enviado' : 'Error' }}
                                  </span>

                                  <!-- Type Badge -->
                                  <span class="text-xs font-medium px-2 py-0.5 rounded flex-shrink-0"
                                    [ngClass]="emailLogService.getEmailTypeClass(email.emailType)">
                                    {{ emailLogService.getEmailTypeLabel(email.emailType) }}
                                  </span>

                                  <!-- Recipient -->
                                  <div class="min-w-0">
                                    <p class="text-sm font-medium text-gray-900 truncate">{{ email.recipientEmail }}</p>
                                    <p *ngIf="email.recipientName" class="text-xs text-gray-500 truncate">{{ email.recipientName }}</p>
                                  </div>
                                </div>

                                <div class="text-right flex-shrink-0 ml-4">
                                  <p class="text-xs text-gray-500">{{ emailLogService.formatDateTime(email.sentAt) }}</p>
                                  <p class="text-xs text-gray-400">{{ email.subject }}</p>
                                </div>
                              </div>

                              <!-- Error message if failed -->
                              <div *ngIf="!email.isSuccess && email.errorMessage" class="mt-2 bg-red-50 border border-red-200 rounded p-2">
                                <p class="text-xs text-red-700">{{ emailLogService.truncateText(email.errorMessage, 200) }}</p>
                              </div>
                            </div>
                          </div>

                          <!-- Tab Content: Webhooks Wompi -->
                          <div *ngIf="isPlanTabActive(plan.id, 'webhooks') && plan.paymentLinkId" class="space-y-3">
                            <div class="flex items-center justify-between mb-3">
                              <h4 class="text-sm font-bold text-gray-700 flex items-center gap-2">
                                <svg class="w-5 h-5 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.858 15.355-5.858 21.213 0" />
                                </svg>
                                Webhooks de Wompi
                              </h4>
                            </div>

                            <!-- Loading -->
                            <div *ngIf="isWebhooksLoading(plan)" class="flex items-center justify-center py-6">
                              <svg class="animate-spin h-5 w-5 text-cyan-500 mr-2" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                              </svg>
                              <span class="text-sm text-gray-500">Cargando webhooks...</span>
                            </div>

                            <!-- Sin webhooks -->
                            <div *ngIf="!isWebhooksLoading(plan) && !hasWebhooks(plan)"
                              class="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                              <svg class="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                              </svg>
                              <p class="text-sm text-gray-500">No se han recibido webhooks para este link de pago.</p>
                            </div>

                            <!-- Lista de webhooks -->
                            <div *ngFor="let webhook of getWebhooksForPlan(plan)"
                              class="bg-white border rounded-lg shadow-sm overflow-hidden">
                              <!-- Webhook Summary Row -->
                              <div class="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                (click)="toggleWebhookDetail(webhook)">
                                <div class="flex items-center justify-between">
                                  <div class="flex items-center gap-3 flex-1 min-w-0">
                                    <!-- Expand/Collapse icon -->
                                    <svg class="w-4 h-4 text-gray-400 transition-transform flex-shrink-0"
                                      [class.rotate-90]="isWebhookExpanded(webhook.id)"
                                      fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                    </svg>

                                    <!-- Transaction Status Badge -->
                                    <span class="text-xs font-bold px-2 py-0.5 rounded"
                                      [class]="webhook.transactionStatus === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
                                               webhook.transactionStatus === 'DECLINED' ? 'bg-red-100 text-red-700' :
                                               webhook.transactionStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                               'bg-gray-100 text-gray-700'">
                                      {{ webhook.transactionStatus || 'N/A' }}
                                    </span>

                                    <!-- Transaction ID -->
                                    <span class="text-xs text-gray-500 font-mono truncate">
                                      TXN: {{ webhook.transactionId }}
                                    </span>

                                    <!-- Date -->
                                    <span class="text-xs text-gray-400 flex-shrink-0">
                                      {{ webhook.requestDate | date:'short' }}
                                    </span>
                                  </div>

                                  <div class="flex items-center gap-3 flex-shrink-0 ml-3">
                                    <!-- Amount -->
                                    <span class="text-xs font-bold text-gray-700">
                                      {{ formatWebhookAmount(webhook.amountInCents, webhook.currency) }}
                                    </span>

                                    <!-- Payment Method -->
                                    <span *ngIf="webhook.paymentMethodType" class="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                      {{ webhook.paymentMethodType }}
                                    </span>

                                    <!-- Processing Status -->
                                    <span class="text-xs font-medium px-2 py-0.5 rounded"
                                      [class]="getWebhookProcessingBadgeClass(webhook.status)">
                                      {{ getWebhookStatusLabel(webhook.status) }}
                                    </span>

                                    <!-- Processing Time -->
                                    <span *ngIf="webhook.processingTimeMs" class="text-xs text-gray-400">
                                      {{ webhook.processingTimeMs }}ms
                                    </span>
                                  </div>
                                </div>

                                <!-- Error message if failed -->
                                <div *ngIf="webhook.errorMessage" class="mt-2 ml-7">
                                  <p class="text-xs text-red-500 truncate">
                                    {{ webhook.failureStep ? '[' + webhook.failureStep + '] ' : '' }}{{ webhook.errorMessage }}
                                  </p>
                                </div>
                              </div>

                              <!-- Webhook Detail (expanded) -->
                              <div *ngIf="isWebhookExpanded(webhook.id)" class="border-t border-gray-100 bg-gray-50 p-4">
                                <!-- Loading detail -->
                                <div *ngIf="webhookDetailLoading[webhook.id]" class="flex items-center py-3">
                                  <svg class="animate-spin h-4 w-4 text-cyan-500 mr-2" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                  </svg>
                                  <span class="text-xs text-gray-500">Cargando detalle...</span>
                                </div>

                                <!-- Detail content -->
                                <div *ngIf="webhookDetail[webhook.id]" class="space-y-4">
                                  <!-- Info Grid -->
                                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">ID Transaccion</p>
                                      <p class="text-xs font-mono font-bold text-gray-700">{{ webhookDetail[webhook.id].transactionId }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Referencia (SKU)</p>
                                      <p class="text-xs font-mono text-gray-700">{{ webhookDetail[webhook.id].reference || 'N/A' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Evento</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].event || 'N/A' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Ambiente</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].environment || 'N/A' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Cliente</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].customerName || 'N/A' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Email</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].customerEmail || 'N/A' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Documento</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].customerLegalIdType || '' }} {{ webhookDetail[webhook.id].customerLegalId || 'N/A' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Info Metodo Pago</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].paymentMethodInfo || 'N/A' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Recibido</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].requestDate | date:'medium' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Procesado</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].processedDate ? (webhookDetail[webhook.id].processedDate | date:'medium') : 'Pendiente' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Finalizado en Wompi</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].transactionFinalizedAt ? (webhookDetail[webhook.id].transactionFinalizedAt | date:'medium') : 'N/A' }}</p>
                                    </div>
                                    <div>
                                      <p class="text-xs text-gray-400 mb-0.5">Tiempo Procesamiento</p>
                                      <p class="text-xs text-gray-700">{{ webhookDetail[webhook.id].processingTimeMs ? webhookDetail[webhook.id].processingTimeMs + 'ms' : 'N/A' }}</p>
                                    </div>
                                  </div>

                                  <!-- Processing Step -->
                                  <div *ngIf="webhookDetail[webhook.id].completedStep || webhookDetail[webhook.id].failureStep"
                                    class="flex items-center gap-2">
                                    <span *ngIf="webhookDetail[webhook.id].completedStep"
                                      class="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                                      Completado: {{ webhookDetail[webhook.id].completedStep }}
                                    </span>
                                    <span *ngIf="webhookDetail[webhook.id].failureStep"
                                      class="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                                      Fallo en: {{ webhookDetail[webhook.id].failureStep }}
                                    </span>
                                  </div>

                                  <!-- Error Details -->
                                  <div *ngIf="webhookDetail[webhook.id].errorMessage"
                                    class="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p class="text-xs font-bold text-red-700 mb-1">Error:</p>
                                    <p class="text-xs text-red-600">{{ webhookDetail[webhook.id].errorMessage }}</p>
                                    <details *ngIf="webhookDetail[webhook.id].errorDetails" class="mt-2">
                                      <summary class="text-xs text-red-500 cursor-pointer hover:text-red-700">Ver detalles tecnicos</summary>
                                      <pre class="text-xs text-red-400 mt-1 whitespace-pre-wrap break-all max-h-32 overflow-y-auto">{{ webhookDetail[webhook.id].errorDetails }}</pre>
                                    </details>
                                  </div>

                                  <!-- Processing Logs -->
                                  <div *ngIf="webhookDetail[webhook.id].processingLogs">
                                    <p class="text-xs font-bold text-gray-600 mb-2">Logs de Procesamiento:</p>
                                    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                      <div *ngFor="let log of parseProcessingLogs(webhookDetail[webhook.id].processingLogs)"
                                        class="px-3 py-1.5 border-b border-gray-100 last:border-b-0 flex items-start gap-2">
                                        <span class="text-xs font-mono text-gray-400 flex-shrink-0 mt-0.5">{{ log.Timestamp }}</span>
                                        <span class="text-xs font-bold flex-shrink-0 mt-0.5" [class]="getLogLevelClass(log.Level)">{{ log.Level }}</span>
                                        <span *ngIf="log.Code" class="text-xs text-gray-400 flex-shrink-0 mt-0.5">[{{ log.Code }}]</span>
                                        <span class="text-xs text-gray-700">{{ log.Message }}</span>
                                      </div>
                                    </div>
                                  </div>

                                  <!-- Action buttons -->
                                  <div class="flex items-center gap-2 pt-2">
                                    <button *ngIf="webhookDetail[webhook.id].rawWebhookData"
                                      (click)="openRawWebhookModal(webhookDetail[webhook.id].rawWebhookData!)"
                                      class="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded transition-colors flex items-center gap-1">
                                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                      </svg>
                                      Ver JSON Completo
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Tab Content: Comprobantes de Ingreso Activos -->
                          <div *ngIf="isPlanTabActive(plan.id, 'receipts')" class="space-y-3">
                            <div class="flex items-center justify-between mb-3">
                              <h4 class="text-sm font-bold text-gray-700 flex items-center gap-2">
                                <svg class="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Comprobantes de Pago Activos
                              </h4>
                              <span class="text-xs text-gray-500">
                                Total: {{ formatCurrencyValue(getTotalActiveReceipts(plan), enrollment?.currencySymbol,
                                enrollment?.currencyCode) }}
                              </span>
                            </div>
                            <div *ngFor="let receipt of getActiveReceipts(plan)"
                              class="bg-white p-4 border border-emerald-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                              <div class="flex items-start justify-between">
                                <div class="flex-1">
                                  <div class="flex items-center gap-2 mb-2">
                                    <span class="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs font-bold rounded">COMPROBANTE #{{ receipt.id }}</span>
                                    <p class="font-bold text-gray-900">{{ receipt.concept }}</p>
                                    <span
                                      class="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                                      Activo
                                    </span>
                                  </div>
                                  <div class="space-y-1 mb-3">
                                    <p class="text-gray-500 text-xs flex items-center">
                                      <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                      <span class="font-medium mr-1">Creación:</span> {{ receipt.createdAt |
                                      date:'mediumDate' }}
                                    </p>
                                    <p class="text-gray-500 text-xs flex items-center">
                                      <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <span class="font-medium mr-1">Pago:</span>
                                      <span *ngIf="receipt.transactionDate">{{ receipt.transactionDate |
                                        date:'mediumDate' }}</span>
                                      <span *ngIf="!receipt.transactionDate" class="text-gray-400 italic">No
                                        disponible</span>
                                    </p>
                                  </div>
                                  <div class="grid grid-cols-3 gap-3 text-xs">
                                    <div class="bg-gray-50 p-2 rounded">
                                      <p class="text-gray-500 mb-1">Bruto</p>
                                      <p class="font-semibold text-gray-900">{{ formatCurrencyValue(receipt.grossAmount,
                                        receipt.currencySymbol, receipt.currencyCode) }}</p>
                                    </div>
                                    <div class="bg-gray-50 p-2 rounded">
                                      <p class="text-gray-500 mb-1">Descuentos</p>
                                      <p class="font-semibold text-red-600">{{ formatCurrencyValue(receipt.totalFees,
                                        receipt.currencySymbol, receipt.currencyCode) }}</p>
                                    </div>
                                    <div class="bg-emerald-50 p-2 rounded">
                                      <p class="text-emerald-600 mb-1 font-medium">Neto</p>
                                      <p class="font-bold text-emerald-700">{{ formatCurrencyValue(receipt.netAmount,
                                        receipt.currencySymbol, receipt.currencyCode) }}</p>
                                    </div>
                                  </div>
                                  <!-- Observaciones del comprobante -->
                                  <div *ngIf="receipt.observations"
                                    class="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                    <p class="text-xs font-medium text-blue-600 mb-1 flex items-center gap-1">
                                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                      Observaciones:
                                    </p>
                                    <p class="text-xs text-blue-900">{{ receipt.observations }}</p>
                                  </div>
                                </div>
                                <div class="ml-4 flex flex-col items-end gap-2 shrink-0">
                                  <a [routerLink]="['/income-receipts/view', receipt.id]" target="_blank"
                                    class="text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1 text-xs font-medium">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg> Ver
                                  </a>

                                  <!-- 🆕 Botón Anular comprobante -->
                                  <button *ngIf="canCancelReceipt(receipt)"
                                    (click)="openCancelReceiptModal(receipt)"
                                    title="Anular este comprobante (revierte el pago de la cuota)"
                                    class="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1 rounded-md transition-colors">
                                    <i class="fas fa-ban"></i>
                                    Anular
                                  </button>
                                  <span *ngIf="!receipt.isCancelled && receipt.isBankReconciled"
                                    title="No se puede anular: comprobante conciliado bancariamente"
                                    class="inline-flex items-center gap-1 text-xs font-medium text-gray-400 cursor-not-allowed opacity-60 px-2 py-1 rounded-md">
                                    <i class="fas fa-ban"></i>
                                    Anular
                                  </span>
                                </div>
                              </div>
                            </div>

                            <!-- Estado vacío -->
                            <div *ngIf="getActiveReceipts(plan).length === 0"
                              class="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                              <svg class="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <p class="text-sm text-gray-500 font-medium">No hay comprobantes de ingreso activos</p>
                            </div>
                          </div>

                          <!-- Tab Content: Devoluciones -->
                          <div *ngIf="isPlanTabActive(plan.id, 'refunds')" class="space-y-3">
                            <div class="flex items-center justify-between mb-3">
                              <h4 class="text-sm font-bold text-gray-700 flex items-center gap-2">
                                <svg class="w-5 h-5 text-purple-500" fill="none" stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                Devoluciones Aplicadas
                              </h4>
                              <span class="text-xs text-purple-600 font-medium">
                                Total devuelto: {{ formatCurrencyValue(getTotalRefundedForPlan(plan),
                                enrollment?.currencySymbol, enrollment?.currencyCode) }}
                              </span>
                            </div>
                            <div *ngFor="let refund of getRefundsForPlan(plan)"
                              class="bg-white p-4 border border-purple-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                              <div class="flex items-start justify-between mb-3">
                                <div class="flex-1">
                                  <div class="flex items-center gap-2 flex-wrap mb-2">
                                    <span class="font-bold text-purple-900 text-sm">{{ refund.documentNumber }}</span>
                                    <span class="px-2 py-0.5 bg-purple-200 text-purple-800 text-xs font-bold rounded">
                                      {{ refund.statusDescription || refund.status }}
                                    </span>
                                    <span class="text-xs text-purple-600">
                                      {{ refund.documentDate | date:'shortDate' }}
                                    </span>
                                  </div>
                                  <p class="text-xs text-purple-700 mb-1">
                                    <span class="font-medium">Concepto:</span> {{ refund.refundConceptDescription ||
                                    refund.refundConcept }}
                                  </p>
                                  <p class="text-xs text-purple-600" *ngIf="refund.justification">
                                    <span class="font-medium">Justificación:</span> {{ refund.justification }}
                                  </p>
                                </div>
                                <a [routerLink]="['/dashboard/refund-vouchers/view', refund.id]" target="_blank"
                                  class="ml-4 text-purple-600 hover:text-purple-800 hover:underline flex items-center gap-1 text-xs font-medium">
                                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg> Ver
                                </a>
                              </div>

                              <!-- Items de devolución relacionados con esta cuota -->
                              <div class="mt-3 pt-3 border-t border-purple-200">
                                <p class="text-xs font-bold text-purple-500 uppercase mb-2">Comprobantes afectados en
                                  esta cuota:</p>
                                <div *ngFor="let item of refund.relatedItems"
                                  class="bg-purple-50 p-3 rounded-lg border border-purple-100 mb-2 last:mb-0">
                                  <div class="flex items-center justify-between">
                                    <div class="flex-1">
                                      <p class="font-medium text-purple-900 text-xs">
                                        Comprobante #{{ item.documentId }}
                                        <span *ngIf="item.incomeReceipts && item.incomeReceipts.length > 0"
                                          class="text-purple-600 font-normal">
                                          - {{ item.incomeReceipts[0].concept }}
                                        </span>
                                      </p>
                                      <p class="text-purple-600 text-[10px] mt-1" *ngIf="item.observations">
                                        {{ item.observations }}
                                      </p>
                                    </div>
                                    <div class="text-right ml-3">
                                      <p class="text-[10px] text-purple-500 mb-1">Monto devuelto</p>
                                      <p class="font-bold text-purple-700 text-sm">
                                        {{ formatCurrencyValue(item.refundedAmount, enrollment?.currencySymbol,
                                        enrollment?.currencyCode) }}
                                      </p>
                                      <p class="mt-1" *ngIf="item.isFullyRefunded">
                                        <span
                                          class="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-medium">Completo</span>
                                      </p>
                                      <p class="mt-1" *ngIf="item.isPartiallyRefunded">
                                        <span
                                          class="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-[10px] font-medium">Parcial</span>
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Estado vacío -->
                            <div *ngIf="!hasRefunds(plan)"
                              class="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                              <svg class="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                              </svg>
                              <p class="text-sm text-gray-500 font-medium">No hay devoluciones aplicadas a esta cuota
                              </p>
                            </div>
                          </div>

                          <!-- Tab Content: Comprobantes Anulados -->
                          <div *ngIf="isPlanTabActive(plan.id, 'cancelled')" class="space-y-3">
                            <div class="flex items-center justify-between mb-3">
                              <h4 class="text-sm font-bold text-gray-700 flex items-center gap-2">
                                <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                                Comprobantes Anulados
                              </h4>
                            </div>
                            <div *ngFor="let receipt of getCancelledReceipts(plan)"
                              class="bg-red-50 p-4 border border-red-200 rounded-lg shadow-sm relative overflow-hidden">
                              <!-- Marca de agua "ANULADO" estilo documento confidencial -->
                              <div class="absolute -inset-[25%] pointer-events-none" style="transform: rotate(-30deg);">
                                <div class="flex flex-col items-center justify-center h-full gap-10">
                                  <span class="text-red-400/15 text-5xl font-black tracking-[0.2em] select-none whitespace-nowrap">ANULADO</span>
                                  <span class="text-red-400/15 text-5xl font-black tracking-[0.2em] select-none whitespace-nowrap">ANULADO</span>
                                  <span class="text-red-400/15 text-5xl font-black tracking-[0.2em] select-none whitespace-nowrap">ANULADO</span>
                                </div>
                              </div>

                              <div class="relative z-10">
                                <div class="flex items-start justify-between mb-3">
                                  <div class="flex-1">
                                    <div class="flex items-center gap-2 mb-2">
                                      <span class="px-2 py-0.5 bg-red-200 text-red-800 text-xs font-bold rounded">COMPROBANTE #{{ receipt.id }}</span>
                                      <p class="font-bold text-red-800 line-through">{{ receipt.concept }}</p>
                                      <span
                                        class="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full">ANULADO</span>
                                    </div>
                                    <div class="space-y-1 mb-2">
                                      <p class="text-red-500 text-xs flex items-center">
                                        <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor"
                                          viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span class="font-medium mr-1">Creado:</span> {{ receipt.createdAt |
                                        date:'mediumDate' }}
                                      </p>
                                      <p class="text-red-500 text-xs flex items-center">
                                        <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor"
                                          viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span class="font-medium mr-1">Pago:</span>
                                        <span *ngIf="receipt.transactionDate">{{ receipt.transactionDate |
                                          date:'mediumDate' }}</span>
                                        <span *ngIf="!receipt.transactionDate" class="text-gray-400 italic">No
                                          disponible</span>
                                      </p>
                                    </div>
                                    <p class="text-red-600 text-xs font-medium" *ngIf="receipt.cancelledAt">
                                      ⛔ Anulado: {{ receipt.cancelledAt | date:'medium' }}
                                    </p>
                                  </div>
                                  <a [routerLink]="['/income-receipts/view', receipt.id]" target="_blank"
                                    class="ml-4 text-red-600 hover:text-red-800 hover:underline flex items-center gap-1 text-xs font-medium">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg> Ver
                                  </a>
                                </div>

                                <div class="grid grid-cols-3 gap-3 text-xs">
                                  <div class="bg-white p-2 rounded border border-red-100">
                                    <p class="text-red-500 mb-1">Monto Anulado</p>
                                    <p class="font-semibold text-red-700 line-through">{{
                                      formatCurrencyValue(receipt.grossAmount, receipt.currencySymbol,
                                      receipt.currencyCode) }}</p>
                                  </div>
                                  <div class="bg-white p-2 rounded border border-red-100">
                                    <p class="text-red-500 mb-1">Anulado por</p>
                                    <p class="font-semibold text-red-700">{{ receipt.cancelledBy || 'N/A' }}</p>
                                  </div>
                                  <div class="bg-white p-2 rounded border border-red-100">
                                    <p class="text-red-500 mb-1">Razón</p>
                                    <p class="font-semibold text-red-700 truncate" [title]="receipt.cancellationReason">
                                      {{ receipt.cancellationReason || 'Sin especificar' }}</p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Estado vacío -->
                            <div *ngIf="getCancelledReceipts(plan).length === 0"
                              class="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                              <svg class="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                              <p class="text-sm text-gray-500 font-medium">No hay comprobantes anulados</p>
                            </div>
                          </div>

                          <!-- Tab Content: Comprobantes Inactivos -->
                          <div *ngIf="isPlanTabActive(plan.id, 'inactive')" class="space-y-3">
                            <div class="flex items-center justify-between mb-3">
                              <h4 class="text-sm font-bold text-gray-700 flex items-center gap-2">
                                <svg class="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                                Comprobantes Inactivos
                                <span class="text-xs text-gray-400 font-normal">(versiones anteriores o pendientes de aprobacion)</span>
                              </h4>
                            </div>
                            <div *ngFor="let receipt of getInactiveReceipts(plan)"
                              class="bg-amber-50 p-4 border border-amber-200 rounded-lg shadow-sm relative overflow-hidden">
                              <!-- Marca de agua -->
                              <div class="absolute -inset-[25%] pointer-events-none" style="transform: rotate(-30deg);">
                                <div class="flex flex-col items-center justify-center h-full gap-10">
                                  <span class="text-amber-400/10 text-5xl font-black tracking-[0.2em] select-none whitespace-nowrap">INACTIVO</span>
                                  <span class="text-amber-400/10 text-5xl font-black tracking-[0.2em] select-none whitespace-nowrap">INACTIVO</span>
                                  <span class="text-amber-400/10 text-5xl font-black tracking-[0.2em] select-none whitespace-nowrap">INACTIVO</span>
                                </div>
                              </div>

                              <div class="relative z-10">
                                <div class="flex items-start justify-between mb-3">
                                  <div class="flex-1">
                                    <div class="flex items-center gap-2 mb-2">
                                      <span class="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs font-bold rounded">#{{ receipt.id }}</span>
                                      <p class="font-bold text-amber-800">{{ receipt.concept }}</p>
                                      <span *ngIf="receipt.approvalStatus === 'PendingApproval'"
                                        class="px-2 py-0.5 bg-yellow-500 text-white text-xs font-bold rounded-full">PENDIENTE APROBACION</span>
                                      <span *ngIf="receipt.approvalStatus !== 'PendingApproval'"
                                        class="px-2 py-0.5 bg-amber-600 text-white text-xs font-bold rounded-full">V{{ receipt.version }}</span>
                                    </div>
                                    <div class="space-y-1 mb-2">
                                      <p class="text-amber-600 text-xs flex items-center">
                                        <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span class="font-medium mr-1">Creado:</span> {{ receipt.createdAt | date:'mediumDate' }}
                                      </p>
                                      <p class="text-amber-600 text-xs flex items-center">
                                        <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span class="font-medium mr-1">Pago:</span>
                                        <span *ngIf="receipt.transactionDate">{{ receipt.transactionDate | date:'mediumDate' }}</span>
                                        <span *ngIf="!receipt.transactionDate" class="text-gray-400 italic">No disponible</span>
                                      </p>
                                      <p *ngIf="receipt.editJustification" class="text-amber-600 text-xs flex items-center">
                                        <svg class="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        <span class="font-medium mr-1">Justificacion:</span> {{ receipt.editJustification }}
                                      </p>
                                    </div>
                                  </div>
                                  <a [routerLink]="['/income-receipts/view', receipt.id]" target="_blank"
                                    class="ml-4 text-amber-600 hover:text-amber-800 hover:underline flex items-center gap-1 text-xs font-medium">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg> Ver
                                  </a>
                                </div>

                                <div class="grid grid-cols-3 gap-3 text-xs">
                                  <div class="bg-white p-2 rounded border border-amber-100">
                                    <p class="text-amber-500 mb-1">Bruto</p>
                                    <p class="font-semibold text-amber-700">{{
                                      formatCurrencyValue(receipt.grossAmount, receipt.currencySymbol,
                                      receipt.currencyCode) }}</p>
                                  </div>
                                  <div class="bg-white p-2 rounded border border-amber-100">
                                    <p class="text-amber-500 mb-1">Descuentos</p>
                                    <p class="font-semibold text-amber-700">{{
                                      formatCurrencyValue(receipt.totalFees, receipt.currencySymbol,
                                      receipt.currencyCode) }}</p>
                                  </div>
                                  <div class="bg-white p-2 rounded border border-amber-100">
                                    <p class="text-amber-500 mb-1">Neto</p>
                                    <p class="font-semibold text-amber-700">{{
                                      formatCurrencyValue(receipt.netAmount, receipt.currencySymbol,
                                      receipt.currencyCode) }}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Tab Content: Historial de Anulaciones -->
                          <div *ngIf="isPlanTabActive(plan.id, 'history')" class="space-y-3">
                            <div class="flex items-center justify-between mb-3">
                              <h4 class="text-sm font-bold text-gray-700 flex items-center gap-2">
                                <svg class="w-5 h-5 text-orange-500" fill="none" stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Historial de Cambios por Anulaciones
                              </h4>
                            </div>
                            <div class="bg-orange-50 p-4 border border-orange-200 rounded-lg">
                              <div class="space-y-4">
                                <div *ngFor="let history of plan.cancellationHistory; let i = index"
                                  class="flex items-start gap-3 pb-3 border-b border-orange-100 last:border-b-0 last:pb-0">
                                  <div
                                    class="flex-shrink-0 w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                    {{ i + 1 }}
                                  </div>
                                  <div class="flex-1 text-sm">
                                    <div class="flex items-center gap-2 flex-wrap mb-2">
                                      <span class="font-medium text-orange-800">{{ history.cancellationDateFormatted ||
                                        (history.cancellationDate | date:'short') }}</span>
                                      <span class="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs font-bold rounded">
                                        {{ history.statusChangeDescription || history.previousPaymentStatus + ' → ' +
                                        history.newPaymentStatus }}
                                      </span>
                                    </div>
                                    <p class="text-orange-700 mb-1">
                                      <span class="font-medium">Monto anulado:</span>
                                      {{ history.cancelledAmountFormatted || (history.cancelledAmount | currency) }}
                                    </p>
                                    <p class="text-orange-600 text-xs mb-1" *ngIf="history.cancellationReason">
                                      <span class="font-medium">Razón:</span> {{ history.cancellationReason }}
                                    </p>
                                    <p class="text-orange-500 text-xs" *ngIf="history.cancelledBy">
                                      <span class="font-medium">Por:</span> {{ history.cancelledBy }}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Estado vacío -->
                            <div *ngIf="!plan.cancellationHistory || plan.cancellationHistory.length === 0"
                              class="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                              <svg class="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p class="text-sm text-gray-500 font-medium">No hay historial de anulaciones</p>
                            </div>
                          </div>
                        </div>

                        <!-- Observaciones de la cuota (siempre visible si existen) -->
                        <div *ngIf="plan.observations && !plan.isPunished"
                          class="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <h4 class="text-xs font-bold text-blue-600 uppercase mb-2 flex items-center gap-2">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Observaciones
                          </h4>
                          <p class="text-sm text-blue-900">{{ plan.observations }}</p>
                        </div>

                        <!-- Información de Castigo (siempre visible si aplica) -->
                        <div *ngIf="plan.isPunished" class="mt-4 bg-red-50 p-4 rounded-lg border border-red-200">
                          <h4 class="text-xs font-bold text-red-600 uppercase mb-3 flex items-center gap-2">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            Información de Castigo
                          </h4>
                          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                            <div>
                              <p class="text-red-600 font-medium text-xs mb-1">Tipo:</p>
                              <p class="text-red-900 font-semibold">{{ getMovementTypeLabel(plan.movementType) }}</p>
                            </div>
                            <div *ngIf="plan.movementDate">
                              <p class="text-red-600 font-medium text-xs mb-1">Fecha:</p>
                              <p class="text-red-900 font-semibold">{{ plan.movementDate | date:'medium' }}</p>
                            </div>
                            <div *ngIf="plan.observations" class="md:col-span-3">
                              <p class="text-red-600 font-medium text-xs mb-1">Observaciones:</p>
                              <p class="text-red-900">{{ plan.observations }}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </ng-container>

              </tbody>
              <tfoot class="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td *ngIf="isPunishMode"></td>
                  <td *ngIf="isSuperAdmin"></td>
                  <td></td>
                  <td colspan="3" class="px-6 py-3 text-xs font-bold text-gray-700 uppercase tracking-wider text-right">
                    Totales ({{ enrollment.paymentPlans?.length || 0 }} cuotas)
                  </td>
                  <td class="px-6 py-3 text-sm font-bold text-gray-900 text-right tabular-nums"
                      title="Total asignado en cuotas">
                    {{ formatCurrencyValue(getActiveInstallmentsSum(), enrollment?.currencySymbol, enrollment?.currencyCode) }}
                  </td>
                  <td class="px-6 py-3 text-sm font-bold text-emerald-600 text-right tabular-nums"
                      title="Total pagado">
                    {{ formatCurrencyValue(enrollment.amountPaid, enrollment?.currencySymbol, enrollment?.currencyCode) }}
                  </td>
                  <td class="px-6 py-3 text-sm font-bold text-amber-600 text-right tabular-nums"
                      title="Saldo pendiente (sin castigo)">
                    {{ formatCurrencyValue(getSaldoPendienteSinCastigo(), enrollment?.currencySymbol, enrollment?.currencyCode) }}
                  </td>
                  <td></td>
                  <td></td>
                </tr>
                <tr *ngIf="getLastPaymentDate()" class="border-t border-gray-100">
                  <td *ngIf="isPunishMode"></td>
                  <td *ngIf="isSuperAdmin"></td>
                  <td colspan="9" class="px-6 py-2 text-xs text-gray-500 text-right">
                    <span class="inline-flex items-center gap-1.5">
                      <svg class="w-3.5 h-3.5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Último pago recibido: <strong class="text-gray-700">{{ getLastPaymentDate() | date:'mediumDate' }}</strong>
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <!-- General Income (Extra payments) -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-lg font-bold text-gray-900">Ingresos Generales</h2>
            <a [routerLink]="['/dashboard/income-receipts/new']"
              [queryParams]="{participantId: enrollment.participant.id, projectId: enrollment.project.id, enrollmentId: enrollment.id}"
              class="text-sm text-indigo-600 hover:text-indigo-800 font-medium">+ Agregar Ingreso</a>
          </div>

          <!-- Receipts List -->
          <div *ngIf="generalIncomeReceipts.length > 0" class="space-y-4">
            <div *ngFor="let receipt of generalIncomeReceipts" class="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div class="flex justify-between items-start">
                <div>
                  <p class="font-bold text-gray-900">{{ receipt.concept }}</p>
                  <p class="text-sm text-gray-500">{{ receipt.createdAt | date:'mediumDate' }}</p>
                </div>
                <div class="text-right">
                  <p class="text-xs text-gray-500">Monto Neto</p>
                  <p class="text-lg font-bold text-green-600">
                    {{ formatCurrencyValue(receipt.netAmount, receipt.currencySymbol, receipt.currencyCode) }}
                  </p>
                </div>
              </div>
              <div class="mt-3 flex justify-end">
                <a [routerLink]="['/income-receipts/view', receipt.id]" target="_blank"
                  class="text-sm text-indigo-600 hover:text-indigo-800 font-medium">Ver comprobante completo &rarr;</a>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div *ngIf="generalIncomeReceipts.length === 0"
            class="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <div class="p-3 rounded-full bg-gray-50 mb-3">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p class="text-sm text-gray-500 font-medium">No hay ingresos adicionales registrados</p>
            <p class="text-xs text-gray-400">Pagos por certificados, materiales, etc.</p>
          </div>
        </section>

        <!-- 🆕 Huella de auditoría: timeline de acciones del módulo -->
        <section *ngIf="enrollment?.id" class="mt-8">
          <app-enrollment-audit-timeline [enrollmentId]="enrollment.id" mode="individual"></app-enrollment-audit-timeline>
        </section>

      </div>
    </div>
  </main>
</div>

<!-- Modal de Detalle de Movimiento -->
<div *ngIf="showMovementModal && selectedMovement" class="fixed inset-0 z-50 overflow-y-auto" role="dialog"
  aria-modal="true">
  <!-- Overlay -->
  <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" (click)="closeMovementModal()"></div>

  <!-- Modal Container -->
  <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
    <!-- Modal Panel -->
    <div
      class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">

      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between"
        [ngClass]="getMovementClass(selectedMovement.movementType)">
        <div class="flex items-center gap-3">
          <div class="p-2 rounded-lg bg-white/50">
            <span class="text-xl">{{ getMovementIcon(selectedMovement.movementType) }}</span>
          </div>
          <div>
            <h3 class="text-lg font-bold">{{ selectedMovement.movementTypeDescription }}</h3>
            <p class="text-sm opacity-80">{{ selectedMovement.movementDate | date:'medium' }}</p>
          </div>
        </div>
        <button (click)="closeMovementModal()" class="text-gray-400 hover:text-gray-600 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="px-6 py-4 bg-white">
        <!-- Información Principal -->
        <div class="grid grid-cols-2 gap-4 mb-4">
          <div class="bg-gray-50 p-3 rounded-lg">
            <p class="text-xs font-medium text-gray-500 mb-1">Monto Afectado</p>
            <p class="text-lg font-bold text-gray-900">{{ selectedMovement.totalAffectedAmount | currency }}</p>
          </div>
          <div class="bg-gray-50 p-3 rounded-lg">
            <p class="text-xs font-medium text-gray-500 mb-1">Cuotas Afectadas</p>
            <p class="text-lg font-bold text-gray-900">{{ selectedMovement.affectedInstallmentsCount }} cuota(s)</p>
          </div>
        </div>

        <!-- Estado Revertido -->
        <div *ngIf="selectedMovement.isReverted" class="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-200">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span class="text-sm font-medium text-gray-700">Este movimiento ha sido revertido</span>
          </div>
        </div>

        <!-- Razón del Movimiento -->
        <div *ngIf="selectedMovement.reason" class="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p class="text-xs font-medium text-blue-600 mb-1 flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Razón
          </p>
          <p class="text-sm text-blue-900">{{ selectedMovement.reason }}</p>
        </div>

        <!-- Notas Adicionales -->
        <div *ngIf="selectedMovement.notes" class="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
          <p class="text-xs font-medium text-purple-600 mb-1 flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Notas Adicionales
          </p>
          <p class="text-sm text-purple-900">{{ selectedMovement.notes }}</p>
        </div>

        <!-- Información de Proyecto Destino -->
        <div *ngIf="selectedMovement.targetProjectName" class="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
          <p class="text-xs font-medium text-green-600 mb-1 flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            Migrado a Proyecto
          </p>
          <p class="text-sm font-semibold text-green-900">{{ selectedMovement.targetProjectName }}</p>
        </div>

        <!-- Usuario que Realizó el Movimiento -->
        <div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <p class="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Realizado por
          </p>
          <p class="text-sm font-medium text-gray-900">{{ selectedMovement.performedBy }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
        <button (click)="closeMovementModal()"
          class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium">
          Cerrar
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Modal de Confirmación de Cambio de Estado -->
<div *ngIf="showStatusConfirmationModal"
  class="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300"
  (click)="onStatusConfirmationCancelled()" (keydown.escape)="onStatusConfirmationCancelled()">
  <div
    class="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transform transition-all duration-300 scale-100"
    (click)="$event.stopPropagation()">

    <!-- Header -->
    <div class="px-6 py-4 bg-gradient-to-r from-yellow-500 to-orange-500">
      <div class="flex items-center gap-3">
        <div class="p-2 bg-white bg-opacity-20 rounded-lg">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 class="text-lg font-bold text-white">{{ statusConfirmationConfig.title }}</h3>
      </div>
    </div>

    <!-- Body -->
    <div class="px-6 py-5">
      <p class="text-gray-700 text-sm whitespace-pre-line mb-4">{{ statusConfirmationConfig.message }}</p>

      <!-- Input de Razón (condicional) -->
      <div *ngIf="showReasonInput" class="mt-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">
          <span class="text-red-500">*</span> Razón del cambio:
        </label>
        <textarea [(ngModel)]="statusChangeReason" rows="3" placeholder="Ingresa la razón para este cambio de estado..."
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm resize-none transition-colors"></textarea>
        <p *ngIf="showReasonInput && !statusChangeReason.trim()" class="text-xs text-red-500 mt-1">
          Este campo es obligatorio
        </p>
      </div>
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
      <button (click)="onStatusConfirmationCancelled()"
        class="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
        {{ statusConfirmationConfig.cancelText }}
      </button>
      <button (click)="onStatusConfirmationConfirmed()" [disabled]="showReasonInput && !statusChangeReason.trim()"
        class="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
        {{ statusConfirmationConfig.confirmText }}
      </button>
    </div>
  </div>
</div>

<!-- Modal de Confirmación de Anulación -->
<div *ngIf="showCancellationModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title"
  role="dialog" aria-modal="true">
  <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"
      (click)="closeCancellationModal()"></div>

    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

    <div
      class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
      <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
        <div class="sm:flex sm:items-start">
          <div
            class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
            <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">Anular Inscripción</h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">
                ¿Estás seguro de que deseas anular esta inscripción? Esta acción revertirá el estado, desactivará los
                planes de pago pendientes y registrará el evento en la auditoría.
              </p>
              <div class="mt-4">
                <label for="reason" class="block text-sm font-medium text-gray-700">Motivo de anulación *</label>
                <textarea id="reason" rows="3" [(ngModel)]="cancellationReason"
                  class="mt-1 shadow-sm focus:ring-red-500 focus:border-red-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Describe el motivo de la anulación..."></textarea>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
        <button type="button" [disabled]="!cancellationReason || isCancelling" (click)="confirmCancellation()"
          class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed">
          {{ isCancelling ? 'Anulando...' : 'Confirmar Anulación' }}
        </button>
        <button type="button" (click)="closeCancellationModal()"
          class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
          Cancelar
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Modal: Raw Webhook JSON -->
<div *ngIf="showRawWebhookModal" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
  <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" (click)="closeRawWebhookModal()"></div>
    <div class="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-3xl sm:w-full">
      <div class="bg-white px-4 pt-5 pb-4 sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
            <svg class="w-5 h-5 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            Webhook JSON Completo
          </h3>
          <button (click)="closeRawWebhookModal()" class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <pre class="bg-gray-900 text-green-400 p-4 rounded-lg text-xs font-mono overflow-auto max-h-96 whitespace-pre-wrap break-all">{{ rawWebhookData }}</pre>
      </div>
      <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
        <button type="button" (click)="closeRawWebhookModal()"
          class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:w-auto sm:text-sm">
          Cerrar
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Modal de Confirmación: Toggle de Cobro Automático -->
<app-confirmation-modal [isVisible]="showAutoPaymentConfirmationModal" [config]="autoPaymentConfirmationConfig"
  (confirmed)="onAutoPaymentConfirmed()" (cancelled)="onAutoPaymentCancelled()">
</app-confirmation-modal>

<!-- 🔐 Modal de operaciones admin sobre cuota (permiso ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS) -->
<div *ngIf="adminModal.open && adminModal.plan"
     class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
  <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h2 class="text-xl font-bold text-gray-900">
          <ng-container [ngSwitch]="adminModal.mode">
            <span *ngSwitchCase="'menu'">Editar cuota — Acciones disponibles</span>
            <span *ngSwitchCase="'renumber'">Renumerar cuota</span>
            <span *ngSwitchCase="'date'">Cambiar fecha de vencimiento</span>
            <span *ngSwitchCase="'delete'">Eliminar cuota</span>
            <span *ngSwitchCase="'status'">Cambiar estado</span>
            <span *ngSwitchCase="'attach'">Asociar comprobante de ingreso</span>
            <span *ngSwitchCase="'recalculate'">Recalcular monto pagado</span>
            <span *ngSwitchCase="'punish-remaining'">Castigar saldo restante</span>
          </ng-container>
        </h2>
        <p class="mt-1 text-xs text-gray-500">
          Cuota actual: <span class="font-semibold">#{{ adminModal.plan.installmentNumber }}</span>
          · Vence {{ adminModal.plan.dueDate | date:'dd/MM/yyyy' }}
          · {{ adminModal.plan.amountToPay | currency:'COP':'symbol-narrow':'1.0-2' }}
        </p>
      </div>
      <button type="button" (click)="closeAdminModal()" class="rounded-full p-1 text-gray-400 hover:bg-gray-100">
        <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- 🆕 GRILLA DE ACCIONES (cuando mode='menu') -->
    <div *ngIf="adminModal.mode === 'menu'" class="mt-5">
      <p class="text-sm text-gray-600 mb-3">Elegí qué querés hacer con esta cuota:</p>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button type="button" (click)="selectAdminMode('recalculate')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-emerald-300 hover:bg-emerald-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Recalcular monto pagado</span>
            <span class="block text-xs text-gray-500">Suma los recibos asociados y actualiza el estado.</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('status')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-teal-300 hover:bg-teal-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Cambiar estado</span>
            <span class="block text-xs text-gray-500">Forzar PAGADO / PARCIAL / PENDIENTE / etc.</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('attach')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-purple-300 hover:bg-purple-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Asociar comprobante</span>
            <span class="block text-xs text-gray-500">Enganchar un IncomeReceipt existente por ID.</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('renumber')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-amber-300 hover:bg-amber-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Renumerar</span>
            <span class="block text-xs text-gray-500">Cambiar el número de cuota (#1, #2…).</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('date')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-sky-300 hover:bg-sky-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Cambiar fecha</span>
            <span class="block text-xs text-gray-500">Modificar la fecha de vencimiento.</span>
          </span>
        </button>

        <button type="button" (click)="selectAdminMode('delete')"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-rose-300 hover:bg-rose-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Eliminar cuota</span>
            <span class="block text-xs text-gray-500">Soft delete (archivar con audit trail).</span>
          </span>
        </button>

        <!-- Castigar saldo restante: sólo tiene sentido si hay saldo > 0 (parcial). -->
        <button type="button" (click)="selectAdminMode('punish-remaining')"
          *ngIf="adminModal.plan && (adminModal.plan.amountToPay - adminModal.plan.amountPaid) > 0 && !adminModal.plan.isPunished && adminModal.plan.paymentStatus !== 'PAID'"
          class="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left hover:border-orange-300 hover:bg-orange-50 transition-colors">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z"/></svg>
          </span>
          <span>
            <span class="block text-sm font-semibold text-gray-900">Castigar saldo restante</span>
            <span class="block text-xs text-gray-500">Cierra la cuota al monto pagado y crea una nueva castigada por el saldo.</span>
          </span>
        </button>
      </div>
    </div>

    <!-- Botón "← Volver" cuando estamos en un modo específico -->
    <div *ngIf="adminModal.mode && adminModal.mode !== 'menu'" class="mt-3">
      <button type="button" (click)="backToAdminMenu()"
        class="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800">
        <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
        Volver a acciones
      </button>
    </div>

    <!-- 🆕 Recalcular monto pagado -->
    <div *ngIf="adminModal.mode === 'recalculate'" class="mt-4">
      <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
        <p class="font-semibold">¿Qué va a pasar?</p>
        <ul class="mt-2 list-disc pl-5 space-y-1 text-xs">
          <li>Se suman los <strong>recibos activos</strong> asociados a esta cuota (vía PaymentPlanId y vía LineageId).</li>
          <li>Se compara con el <strong>monto a pagar</strong> de la cuota.</li>
          <li>Se setea el estado correcto: PENDIENTE / PARCIALMENTE PAGADO / PAGADO.</li>
          <li><strong>No requiere comentario</strong> — es una operación idempotente y segura.</li>
        </ul>
      </div>
    </div>

    <!-- 🆕 Castigar saldo restante -->
    <div *ngIf="adminModal.mode === 'punish-remaining' && adminModal.plan" class="mt-4 space-y-3">
      <div class="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
        <p class="font-semibold">¿Qué va a pasar?</p>
        <ul class="mt-2 list-disc pl-5 space-y-1 text-xs">
          <li>La cuota <strong>#{{ adminModal.plan.installmentNumber }}</strong> se cierra al monto efectivamente pagado
            (<strong>{{ formatCurrencyValue(adminModal.plan.amountPaid, enrollment?.currencySymbol, enrollment?.currencyCode) }}</strong>)
            y queda <strong>PAGADA</strong> + bloqueada.</li>
          <li>Se crea una <strong>nueva cuota castigada</strong> por el saldo restante
            (<strong>{{ formatCurrencyValue((adminModal.plan.amountToPay - adminModal.plan.amountPaid), enrollment?.currencySymbol, enrollment?.currencyCode) }}</strong>),
            con la misma fecha de vencimiento ({{ adminModal.plan.dueDate | date:'mediumDate' }}).</li>
          <li>Se registra un <strong>movimiento de cartera</strong> (ManualPunishment) con motivo, para trazabilidad.</li>
          <li>La nueva cuota va al <strong>final del plan</strong> (no renumera las existentes).</li>
        </ul>
      </div>
      <label class="block text-sm font-semibold text-gray-800">Motivo del castigo</label>
      <textarea rows="3" [(ngModel)]="adminModal.comment"
        placeholder="Ej: Saldo no cobrable, participante fuera de contacto, etc."
        class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"></textarea>
      <p class="text-xs text-gray-500">Mínimo 10 caracteres. Queda en el PortfolioMovement y en el audit trail de ambas cuotas.</p>
    </div>

    <!-- Inputs por modo -->
    <div *ngIf="adminModal.mode === 'renumber'" class="mt-4">
      <label class="block text-sm font-semibold text-gray-800">Nuevo número de cuota</label>
      <input type="number" min="1" [(ngModel)]="adminModal.newNumber"
        class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
      <p class="mt-1 text-xs text-gray-500">No puede chocar con otra cuota activa del mismo enrollment.</p>
    </div>

    <div *ngIf="adminModal.mode === 'date'" class="mt-4">
      <label class="block text-sm font-semibold text-gray-800">Nueva fecha de vencimiento</label>
      <input type="date" [(ngModel)]="adminModal.newDate"
        class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
    </div>

    <div *ngIf="adminModal.mode === 'status'" class="mt-4">
      <label class="block text-sm font-semibold text-gray-800">Nuevo estado</label>
      <select [(ngModel)]="adminModal.newStatus"
        class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
        <option value="AUTO">🔄 Auto (recalcular desde recibos)</option>
        <option value="PENDING">⏳ Pendiente</option>
        <option value="PAID">✅ Pagado</option>
        <option value="PARTIALLY_PAID">🟡 Parcialmente pagado</option>
        <option value="OVERDUE">🔴 Vencido</option>
        <option value="LINK_GENERATED">🔗 Link generado</option>
      </select>
      <div class="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
        <p class="font-semibold">¿Qué hace "Auto"?</p>
        <p class="mt-1">El backend suma los recibos activos asociados a esta cuota (vía PaymentPlanId o LineageId) y determina el estado correcto:</p>
        <ul class="mt-1 list-disc pl-5 space-y-0.5">
          <li><strong>Sin recibos</strong> → PENDIENTE</li>
          <li><strong>0 &lt; recibos &lt; monto</strong> → PARCIALMENTE PAGADO</li>
          <li><strong>recibos ≥ monto</strong> → PAGADO (y se lockea)</li>
        </ul>
      </div>
    </div>

    <div *ngIf="adminModal.mode === 'attach'" class="mt-4">
      <label class="block text-sm font-semibold text-gray-800">ID del comprobante de ingreso</label>
      <input type="number" min="1" [(ngModel)]="adminModal.attachReceiptId"
        placeholder="Ej: 5406"
        class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono" />
      <div class="mt-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-xs text-purple-900">
        <p class="font-semibold">¿Qué hace esta acción?</p>
        <ul class="mt-1 list-disc pl-5 space-y-0.5">
          <li>Reasocia el <strong>IncomeReceipt #{{ adminModal.attachReceiptId || 'X' }}</strong> a esta cuota.</li>
          <li>Una cuota puede tener N comprobantes — esta acción <strong>agrega</strong>, no reemplaza los existentes.</li>
          <li>Si el recibo estaba en otra cuota individual, se quita de allá y se mueve aquí.</li>
          <li>Si estaba en una cuota corporativa o de otra inscripción, requiere el checkbox de abajo para confirmar.</li>
          <li>Tras asociar, el status de ambas cuotas (vieja y nueva) se recalcula automáticamente.</li>
        </ul>
      </div>
      <label class="mt-3 flex cursor-pointer items-start gap-2 text-xs text-gray-700">
        <input type="checkbox" [(ngModel)]="adminModal.forceFromOtherEnrollment" class="mt-0.5">
        <span>Forzar reasignación incluso si el comprobante pertenece a OTRA inscripción o a una cuota corporativa.</span>
      </label>
    </div>

    <div *ngIf="adminModal.mode === 'delete'" class="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
      <p class="font-semibold">Vas a eliminar (soft delete) esta cuota.</p>
      <p class="mt-1 text-xs">La cuota queda <span class="font-mono">IsActive=false, IsDeleted=true</span>, archivada con audit trail. Si tiene recibos asociados, te va a pedir confirmación explícita.</p>
      <label class="mt-3 flex cursor-pointer items-start gap-2 text-xs">
        <input type="checkbox" [(ngModel)]="adminModal.forceIfHasReceipts" class="mt-0.5">
        <span>Forzar eliminación incluso si la cuota tiene recibos asociados (los recibos quedarán huérfanos y van a aparecer en el audit dashboard).</span>
      </label>
    </div>

    <!-- Comentario obligatorio (solo para acciones que mutan datos) -->
    <div *ngIf="adminModal.mode && adminModal.mode !== 'menu' && adminModal.mode !== 'recalculate'" class="mt-5">
      <label class="block text-sm font-semibold text-gray-800">Comentario para audit trail (obligatorio)</label>
      <textarea [(ngModel)]="adminModal.comment" rows="3" maxlength="2000"
        placeholder="Ej: Corrección manual previa a refinanciamiento — duplicado del bug de versionado viejo."
        class="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"></textarea>
      <p class="mt-1 text-xs text-gray-500">{{ (adminModal.comment || '').length }} / 2000 (mín. 10)</p>
    </div>

    <!-- Footer: solo "Cerrar" si estoy en el menú; "Cancelar + Confirmar" si estoy en un modo -->
    <div class="mt-6 flex flex-col gap-3 sm:flex-row">
      <button type="button" (click)="closeAdminModal()"
        class="inline-flex flex-1 items-center justify-center rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">
        {{ adminModal.mode === 'menu' ? 'Cerrar' : 'Cancelar' }}
      </button>
      <button *ngIf="adminModal.mode && adminModal.mode !== 'menu'"
        type="button" (click)="confirmAdminAction()"
        [ngClass]="(adminModal.mode === 'delete' || adminModal.mode === 'punish-remaining')
          ? 'border-rose-700 bg-rose-700 text-white hover:bg-rose-800'
          : 'border-gray-900 bg-gray-900 text-white hover:bg-gray-800'"
        class="inline-flex flex-1 items-center justify-center rounded-full border px-4 py-2 text-sm font-semibold">
        <ng-container [ngSwitch]="adminModal.mode">
          <span *ngSwitchCase="'renumber'">Renumerar</span>
          <span *ngSwitchCase="'date'">Cambiar fecha</span>
          <span *ngSwitchCase="'delete'">Eliminar cuota</span>
          <span *ngSwitchCase="'status'">Cambiar estado</span>
          <span *ngSwitchCase="'attach'">Asociar comprobante</span>
          <span *ngSwitchCase="'recalculate'">Recalcular ahora</span>
          <span *ngSwitchCase="'punish-remaining'">Castigar saldo</span>
        </ng-container>
      </button>
    </div>
  </div>
</div>

<!-- 🆕 Modal de confirmación de anulación de comprobante -->
<div *ngIf="cancelReceiptModal.open" class="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true"
  aria-labelledby="cancel-receipt-modal-title">
  <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-300"
    (click)="closeCancelReceiptModal()"></div>
  <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0 relative">
    <div
      class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all duration-300 ease-out sm:my-8 sm:w-full sm:max-w-lg">
      <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
        <div class="sm:flex sm:items-start">
          <div
            class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <i class="fas fa-ban text-red-600 text-xl"></i>
          </div>
          <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
            <h3 class="text-base font-semibold leading-6 text-gray-900" id="cancel-receipt-modal-title">
              Anular Comprobante de Ingreso
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500 leading-relaxed">
                Esta acción revierte el pago en la cuota asociada (la deja como
                <strong>Pendiente</strong> o <strong>Parcialmente pagada</strong> según corresponda)
                y registra el evento en el historial. No se puede deshacer.
              </p>
            </div>
            <div *ngIf="cancelReceiptModal.receipt" class="mt-3 bg-gray-50 rounded-lg p-3">
              <div class="text-sm text-gray-700">
                <p><strong>Comprobante:</strong> #{{ cancelReceiptModal.receipt.id }}</p>
                <p *ngIf="cancelReceiptModal.receipt.concept">
                  <strong>Concepto:</strong> {{ cancelReceiptModal.receipt.concept }}
                </p>
                <p><strong>Monto bruto:</strong>
                  {{ formatCurrencyValue(cancelReceiptModal.receipt.grossAmount,
                     cancelReceiptModal.receipt.currencySymbol, cancelReceiptModal.receipt.currencyCode) }}
                </p>
                <p *ngIf="cancelReceiptModal.receipt.transactionDate">
                  <strong>Fecha de pago:</strong>
                  {{ cancelReceiptModal.receipt.transactionDate | date:'mediumDate' }}
                </p>
              </div>
            </div>
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Motivo de anulación <span class="text-red-600">*</span>
              </label>
              <textarea [(ngModel)]="cancelReceiptModal.reason"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                rows="3" placeholder="Especifique el motivo de la anulación (mín. 10 caracteres)..."
                maxlength="1000" [disabled]="cancelReceiptModal.submitting" required></textarea>
              <div class="mt-1 text-xs text-gray-500">
                {{ (cancelReceiptModal.reason.length || 0) }}/1000 caracteres
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
        <button type="button" [disabled]="cancelReceiptModal.submitting"
          class="w-full inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          (click)="confirmCancelReceipt()">
          <i class="fas fa-ban mr-2"></i>
          {{ cancelReceiptModal.submitting ? 'Anulando...' : 'Confirmar Anulación' }}
        </button>
        <button type="button" [disabled]="cancelReceiptModal.submitting"
          class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-colors duration-200 disabled:opacity-50"
          (click)="closeCancelReceiptModal()">
          Cancelar
        </button>
      </div>
    </div>
  </div>
</div>
<!-- ============================================================ -->
<!-- 🆕 Modal editar valor de matrícula (ENROLLMENTS_PAYMENT_PLAN_ADMIN_OPS) -->
<!-- ============================================================ -->
<div *ngIf="editAmountModal.open" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
  <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
    <div class="flex items-start justify-between gap-3">
      <h2 class="text-xl font-bold text-gray-900">Editar valor de matrícula</h2>
      <button type="button" (click)="closeEditAmountModal()" class="rounded-full p-1 text-gray-400 hover:bg-gray-100">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div *ngIf="editAmountModal.loading" class="mt-6 text-sm text-gray-500">Cargando referencia del proyecto…</div>

    <ng-container *ngIf="!editAmountModal.loading && editAmountModal.context">
      <!-- Paso 1: edición -->
      <div *ngIf="!editAmountModal.confirmStep" class="mt-4 space-y-3 text-sm">
        <div class="rounded-lg bg-gray-50 p-3 space-y-1">
          <div class="flex justify-between"><span class="text-gray-500">Valor de referencia del proyecto</span>
            <span class="font-semibold">{{ formatCurrencyValue(editAmountModal.context.referenceAmount, enrollment?.currencySymbol, enrollment?.currencyCode) }}</span></div>
          <div class="flex justify-between"><span class="text-gray-500">Valor actual</span>
            <span class="font-semibold">{{ formatCurrencyValue(enrollment.totalAmount, enrollment?.currencySymbol, enrollment?.currencyCode) }}</span></div>
          <div class="flex justify-between"><span class="text-gray-500">Descuento actual</span>
            <span class="font-semibold">{{ enrollment.discount | number:'1.0-2' }}%</span></div>
          <div class="flex justify-between"><span class="text-gray-500">Descuento máximo permitido</span>
            <span class="font-semibold">{{ editAmountModal.context.maxPercentDiscount }}%</span></div>
        </div>

        <label class="block">
          <span class="font-medium text-gray-700">Nuevo valor</span>
          <input type="text" inputmode="numeric" appCurrencyFormatter [(ngModel)]="editAmountModal.newValue"
                 class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2" />
        </label>

        <div class="rounded-lg bg-emerald-50 p-3 text-emerald-800">
          Nuevo descuento: <span class="font-bold">{{ editAmountPreview.discount | number:'1.0-2' }}%</span>
          · −{{ formatCurrencyValue(editAmountPreview.discountAmount, enrollment?.currencySymbol, enrollment?.currencyCode) }}
          <div *ngIf="editAmountPreview.aboveReference" class="mt-1 text-xs">El valor supera la referencia: queda sin descuento (0%).</div>
        </div>

        <label class="block">
          <span class="font-medium text-gray-700">Comentario (obligatorio, mín. 10 caracteres)</span>
          <textarea rows="2" [(ngModel)]="editAmountModal.comment"
                    class="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"></textarea>
        </label>

        <div class="flex justify-end gap-2 pt-2">
          <button type="button" (click)="closeEditAmountModal()" class="rounded-lg border border-gray-300 px-4 py-2">Cancelar</button>
          <button type="button" (click)="confirmEditAmount()"
                  class="rounded-lg bg-sky-600 px-4 py-2 font-semibold text-white disabled:opacity-40">Continuar</button>
        </div>
      </div>

      <!-- Paso 2: confirmación -->
      <div *ngIf="editAmountModal.confirmStep" class="mt-4 space-y-4 text-sm">
        <div class="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
          <div class="font-semibold">¿Confirmas el cambio?</div>
          <div class="mt-1">
            El valor pasará de
            <b>{{ formatCurrencyValue(enrollment.totalAmount, enrollment?.currencySymbol, enrollment?.currencyCode) }}</b> a
            <b>{{ formatCurrencyValue(editAmountModal.newValue ?? 0, enrollment?.currencySymbol, enrollment?.currencyCode) }}</b>
            y el descuento de <b>{{ enrollment.discount | number:'1.0-2' }}%</b> a
            <b>{{ editAmountPreview.discount | number:'1.0-2' }}%</b>.
            Después deberás <b>editar el plan de pagos</b> para redistribuir los saldos de las cuotas.
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button type="button" (click)="editAmountModal.confirmStep = false" [disabled]="editAmountModal.saving"
                  class="rounded-lg border border-gray-300 px-4 py-2">Volver</button>
          <button type="button" (click)="saveEditAmount()" [disabled]="editAmountModal.saving"
                  class="rounded-lg bg-sky-600 px-4 py-2 font-semibold text-white disabled:opacity-40">
            {{ editAmountModal.saving ? 'Guardando…' : 'Sí, guardar' }}
          </button>
        </div>
      </div>
    </ng-container>
  </div>
</div>

<!-- ============================================================ -->
<!-- 🆕 Modal papelera de cuotas eliminadas (ENROLLMENTS_PAYMENT_PLAN_TRASH) -->
<!-- ============================================================ -->
<div *ngIf="trashModal.open" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
  <div class="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
    <div class="flex items-start justify-between gap-3">
      <div>
        <h2 class="text-xl font-bold text-gray-900">Papelera de cuotas</h2>
        <p class="mt-1 text-xs text-gray-500">Cuotas eliminadas de esta inscripción. Restaurar las reasigna a la versión activa del plan.</p>
      </div>
      <button type="button" (click)="trashModal.open = false" class="rounded-full p-1 text-gray-400 hover:bg-gray-100">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div *ngIf="trashModal.items.length === 0" class="mt-6 text-sm text-gray-500">No hay cuotas eliminadas.</div>

    <div *ngIf="trashModal.items.length > 0" class="mt-4 max-h-96 overflow-y-auto">
      <table class="w-full text-sm">
        <thead class="text-left text-xs text-gray-500">
          <tr>
            <th class="py-1.5 pr-2">#</th>
            <th class="py-1.5 pr-2">Monto</th>
            <th class="py-1.5 pr-2">Vencimiento</th>
            <th class="py-1.5 pr-2">Eliminada</th>
            <th class="py-1.5 pr-2">Por</th>
            <th class="py-1.5"></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of trashModal.items" class="border-t border-gray-100">
            <td class="py-2 pr-2 font-semibold">#{{ item.installmentNumber }}</td>
            <td class="py-2 pr-2 tabular-nums">{{ formatCurrencyValue(item.amountToPay, enrollment?.currencySymbol, enrollment?.currencyCode) }}</td>
            <td class="py-2 pr-2">{{ item.dueDate | date:'dd/MM/yyyy' }}</td>
            <td class="py-2 pr-2">{{ item.deletedAt ? (item.deletedAt | date:'dd/MM/yyyy HH:mm') : '—' }}</td>
            <td class="py-2 pr-2 text-xs">{{ item.deletedBy ?? '—' }}</td>
            <td class="py-2 text-right">
              <button type="button" (click)="askRestore(item)"
                      class="rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
                Restaurar
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Confirmación de restauración -->
    <div *ngIf="trashModal.restoring" class="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
      <div class="font-semibold text-emerald-800">
        Restaurar cuota #{{ trashModal.restoring.installmentNumber }}
        ({{ formatCurrencyValue(trashModal.restoring.amountToPay, enrollment?.currencySymbol, enrollment?.currencyCode) }})
      </div>
      <p class="mt-1 text-xs text-emerald-700">Volverá activa en la versión actual del plan y se recalculará su estado de pago.</p>
      <textarea rows="2" [(ngModel)]="trashModal.comment" placeholder="Comentario obligatorio (mín. 10 caracteres)"
                class="mt-2 w-full rounded-lg border border-emerald-300 px-3 py-2"></textarea>
      <div class="mt-2 flex justify-end gap-2">
        <button type="button" (click)="trashModal.restoring = null" [disabled]="trashModal.saving"
                class="rounded-lg border border-gray-300 px-3 py-1.5 text-xs">Cancelar</button>
        <button type="button" (click)="confirmRestore()" [disabled]="trashModal.saving"
                class="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
          {{ trashModal.saving ? 'Restaurando…' : 'Confirmar restauración' }}
        </button>
      </div>
    </div>
  </div>
</div>
